import { addObserver, removeObserver } from '@ember/object/observers';
import { schedule } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { underscore } from '@ember/string';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';

import DOMPurify from 'dompurify';
import moment from 'moment';

import type { APIData, Billing, PopupData, RequestError, Service } from 'webapp-types';
import {
	BillingAddonType,
	BillingCycle,
	BillingFeature,
	BillingPaymentMethod,
	BillingServiceType,
	PlanExtrasType,
	PlanFeatureItem,
	PlanType,
	PromotionIdentifier,
} from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { capitalize, equal, getTs, redirect } from 'webapp/utils/common';
import { getFormattedMonthlyPrice, getFormattedPrice, getFormattedTime } from 'webapp/utils/number';

import BillingPopupAddonCancelComponent from 'webapp/components/billing/popup-addon-cancel';
import BillingPopupAddonPurchaseComponent from 'webapp/components/billing/popup-addon-purchase';
import BillingPopupAgencyBlockerComponent from 'webapp/components/billing/popup-agency-blocker';
import BillingPopupAgencyDowngradeComponent from 'webapp/components/billing/popup-agency-downgrade';
import BillingPopupAiCreditsPurchaseComponent from 'webapp/components/billing/popup-ai-credits-purchase';
import BillingPopupAiCreditsUsageComponent from 'webapp/components/billing/popup-ai-credits-usage';
import BillingPopupAnnualCommitmentInfoComponent from 'webapp/components/billing/popup-annual-commitment-info';
import BillingPopupCardExpiringComponent from 'webapp/components/billing/popup-card-expiring';
import BillingPopupClientPacksPurchaseComponent from 'webapp/components/billing/popup-client-packs-purchase';
import type { ExtraItem } from 'webapp/components/billing/popup-confirm';
import BillingPopupConfirmComponent from 'webapp/components/billing/popup-confirm';
import BillingPopupExpiredTrialComponent from 'webapp/components/billing/popup-expired-trial';
import BillingPopupForbiddenFeatureComponent from 'webapp/components/billing/popup-forbidden-feature';
import BillingPopupFreeTrialRequestComponent from 'webapp/components/billing/popup-free-trial-request';
import BillingPopupHigherPlanTrialExpiredComponent from 'webapp/components/billing/popup-higher-plan-trial-expired';
import BillingPopupInvoiceDowngradeComponent from 'webapp/components/billing/popup-invoice-downgrade';
import BillingPopupInvoicesComponent from 'webapp/components/billing/popup-invoices';
import BillingPopupPaymentFailedComponent from 'webapp/components/billing/popup-payment-failed';
import BillingPopupPlanUpgradeFeatureComponent from 'webapp/components/billing/popup-plan-upgrade-feature';
import BillingPopupRequestUpgradeComponent from 'webapp/components/billing/popup-request-upgrade';
import BillingPopupSecondTrialComponent from 'webapp/components/billing/popup-second-trial';
import BillingPopupServicesAndAddonsComponent from 'webapp/components/billing/popup-services-and-addons';
import BillingPppupServicesConfirmComponent from 'webapp/components/billing/popup-services-confirm';
import BillingPopupTrialActivatedComponent from 'webapp/components/billing/popup-trial-activated';
import BillingPromotionsGrowth2024PopupPlanUpgradeOfferComponent from 'webapp/components/billing/promotions/growth-2024/popup-plan-upgrade-offer';

import ActiveFeatures from 'webapp/models-custom/active-features';
import BillingPlan, { VERSION_ID_SEPT_2026 } from 'webapp/models-custom/billing-plan';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type SourceType from 'webapp/models-custom/source-type';
import type {
	SubscriptionData,
	SubscriptionItem,
	SubscriptionScheduledChanges,
} from 'webapp/models-custom/subscription';
import Subscription, { SubscriptionStatus } from 'webapp/models-custom/subscription';

import { Defaults } from 'webapp/services/account';
import { Severity } from 'webapp/services/logger';

type CountableFeatureName =
	| BillingFeature.BOARDS
	| BillingFeature.SOURCES
	| BillingFeature.USERS
	| BillingFeature.CUSTOM_METRICS
	| BillingFeature.ADVANCED_DATA_SOURCES
	| BillingFeature.ADVANCED_DATA_SOURCES_LIMITED
	| BillingFeature.SQL_SOURCES
	| BillingFeature.SHEET_SOURCES
	| BillingFeature.CLIENTS
	| BillingFeature.AI_CREDITS
	| BillingFeature.REPORTS_BASIC;

export type TrackingOriginTypeForTrialPopupOpened =
	| 'onboarding'
	| 'datasource_limitation'
	| 'feature_limitation'
	| 'feature_limitation_reports'
	| 'count_limitation'
	| 'pricing_page'
	| 'billing_page'
	| 'top_banner'
	| 'feature_limitation_forecasting'
	| 'designer_media_creatives'
	| 'generative_ai_block'
	| 'genie_chat'
	| 'datasets'
	| 'limit'
	| 'offer';

export type TrackingOriginForSubscriptionChangePopup =
	| 'top_banner'
	| 'pricing_page'
	| 'pricing_page_upgrade'
	| 'billing_page'
	| 'billing_page_cta'
	| 'promote_growth_plan_popup'
	| 'promote_growth_plan_top_banner'
	| 'email_upgrade_request'
	| 'limitation_popup'
	| 'generative_ai_block'
	| 'paid_trial_expired_popup'
	| 'paid_trial_banner'
	| 'paid_trial_email'
	| 'datasets_trial_expired';

export type TrackingOriginSubscriptionType = 'free' | 'trial' | 'customer';

const BillingCallbackStateOptionKeys = [
	'openServicesAndAddonsPopup',
	'openGrowthPaidTrialPopup',
] as const;

type BillingCallbackStateOptionKeysType = (typeof BillingCallbackStateOptionKeys)[number];

export type ChangePlanStateOptions = Partial<Record<BillingCallbackStateOptionKeysType, boolean>>;

export interface ExtraObject {
	productPriceId: string;
	quantity: number;
}

interface ChangePlanData extends ChangePlanStateOptions {
	planId: string;
	extras?: ExtraObject[];
	promotionExtras?: ExtraObject[];
	trialing?: boolean;
	promotionId?: PromotionIdentifier;
}

export interface PaymentMethodData {
	type: BillingPaymentMethod;
	id?: string; // all except INVOICE
	expMonth?: number; // for CC
	expYear?: number; // for CC
	holderName?: string; // for CC
	last4?: string; // for CC
	brand?: string; // for CC
	fullName: string; // for BANK_ACC
	email: string; // for BANK_ACC
}

export interface RawInvoiceData {
	billingCycle?: BillingCycle;
	total?: number;
	items?: SubscriptionItem[];
	periodStart?: number;
	periodEnd?: number;
}

export interface ParsedInvoiceData {
	total: number;
	totalFormatted: string;
	totalMonthlyFormatted: string;
	items: SubscriptionItem[];
	billingCycle: string;
	billingCycleDiscount: number;
	description: string;
	monthsInCycle: number;
	periodStart: number;
	periodEnd: number;
}

interface CommitmentInfo {
	startDate: number; // start of commitment
	until: number; // end of commitment (snap to billing date)
	actionDate: number; // 30/45 days before "until" - this marks the end of downgrade / cancel freeze
}

export interface TopUpData {
	id: string;
	quantity: number; // current selection (what user will pay for next month and forward)
	grantedQuantity: number; // granted amount for this period - e.g. user paid for 5 and changed to 4 in the same period - quantity is 4 but granted is still 5. So if they change from 4 to 6 still in the same month, they'll pay for 1 (6-5) instead of 2 (6-4)
}

export interface BillingInfoData {
	agencyPricing?: boolean;
	activeUntil?: number;
	becamePaying?: number;
	customerId?: string;
	billingEmail?: string;
	failedPaymentCnt?: number;
	trialCount?: number;
	trialRequestCnt?: number;
	discount?: number | null;
	paymentMethod?: PaymentMethodData | null;
	isProviderProcessing?: boolean;
	grandFathered?: boolean;
	upcomingInvoice?: RawInvoiceData | null;
	custom?: {
		boards?: number;
		customMetrics?: number;
		sources?: number;
		minimumBillable?: number;
	} | null;
	failureMessage?: string | null;
	commitment?: CommitmentInfo | null;
	eligibleForMigration?: boolean;
	lastPayingPlan?: string;
	nextSubscription?: MinimalSubscriptionData | null;
	previousSubscription?: MinimalSubscriptionData | null;
	savingsFromLegacy?: number | null;
	version?: string | null;
	subscriptions?: SubscriptionData[];
	topUps: TopUpData[]; // seperate from subscriptions due to not charging with the subscription period but always monthly
	resetAiCreditsAt?: number;
}

interface MinimalSubscriptionData {
	items: SubscriptionItem[];
}

interface PlanFeatureItemInfo {
	name: string;
	description: string;
	hideOn2026SeptView?: boolean;
	isSoon?: boolean;
}

interface EnabledPlanFeatureItems {
	title: string;
	items: PlanFeatureItemInfo[];
}

// the static part of an addon's info
type AddonBaseInfo = Pick<Billing.Addon, 'name' | 'description' | 'icon' | 'learnMoreUrl'> & {
	alwaysMonthly?: boolean;
};

type ServiceBaseInfo = Pick<
	Billing.ServiceInfo,
	'name' | 'description' | 'icon' | 'bookCallLink' | 'learnMoreUrl'
>;

export default class BillingService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('tour') declare tourService: Service.Tour;
	@service('store') declare storeService: Service.Store;
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('timer') declare timerService: Service.Timer;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('flow/annual-plan') declare flowAnnualPlanService: Service.Flow.AnnualPlan;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	// updated from billingInfo
	@tracked agencyPricing = false;
	@tracked activeUntil!: number;
	@tracked becamePaying!: number;
	@tracked customerId!: string;
	@tracked billingEmail!: string;
	@tracked failedPaymentCnt = 0;
	@tracked trialCount = 0;
	@tracked trialRequestCnt = 0;
	@tracked monthlyDiscount = 0; // in cents, per month
	@tracked paymentMethod: PaymentMethodData | null = null;
	@tracked isProviderProcessing = false;
	@tracked upcomingInvoiceData: ParsedInvoiceData | null = null;
	@tracked failureMessage: string | null = null; // https://stripe.com/docs/declines/codes - Codes with messages
	@tracked commitment: CommitmentInfo | null = null;
	@tracked resetAiCreditsAt: number | null = null;
	@tracked version = '';
	@tracked topUps: TopUpData[] = [];

	@tracked grandFathered = false;

	/*
		eligibleForMigration is false for:
		- excluded customers because of special deals, terms (will be handled manually)
		- regular customers already on the new plan and won't be migrated anymore
	*/
	@tracked eligibleForMigration = false;

	@tracked lastPayingPlan!: string;
	@tracked nextSubscription: ParsedInvoiceData | null = null;
	@tracked previousSubscription: ParsedInvoiceData | null = null;
	@tracked savingsFromLegacy = 0;

	@tracked subscription!: Subscription;
	@tracked higherPlanTrialSubscription: Subscription | null = null; // currently unused
	@tracked scheduledSubscriptionChanges: SubscriptionScheduledChanges | null = null;

	@tracked customChanges: {
		boards?: number;
		customMetrics?: number;
		sources?: number;
		minimumBillable?: number;
		ai_credits?: number;
		clients?: number;
	} = {}; // custom (PA) given amounts. Don't use if possible - only sources cannot be calculated in any other way than from this (bcos plan max is unlimited)

	// updated from featureInfo
	features: ActiveFeatures;

	// set from fetchBillingOptions
	@tracked allPlans: BillingPlan[] = [];

	// set from available-plans
	@tracked aiCreditsInOnePack = 0;
	@tracked featuresInOneClientPack = {
		[BillingFeature.CLIENTS]: 0,
		[BillingFeature.SOURCES]: 0,
		[BillingFeature.AI_CREDITS]: 0,
	};

	annualCommitmentCallUrl =
		'https://meetings.hubspot.com/databox-team/annual-commitment?uuid=e64a6c85-9a69-4bda-a60d-19a5851a0d70';

	customPlanMeetingUrl = 'https://meetings.hubspot.com/databox-team/custom-plan';

	analystPlanVisibleForCreatedAfter = getTs('2026-05-27 6:20:00'); // UTC

	@tracked skipBannerForMigration = false;

	billingCycleDiscounts = {
		monthly: 0,
		quarterly: 10,
		yearly: 20,
	};

	// second trial
	@tracked isRequestSecondTrialInProgress = false;

	// temporary hide activity log addon, until Advanced Security addon will be implemented
	allAddons: BillingAddonType[];

	@tracked invalidateFeatureCache?: () => void = undefined;

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.allAddons = Object.keys(BillingService.addonInfos).filter(
			(addon) => addon !== BillingAddonType.ACTIVITY_LOG,
		) as BillingAddonType[];

		this.features = new ActiveFeatures();
	}

	get is2026SeptBillingGroup() {
		return this.version === VERSION_ID_SEPT_2026;
	}

	get planId(): string {
		return this.subscription?.planId;
	}

	get activePlan(): BillingPlan | null {
		// current plan
		return this.planOnReactivate || this.findPlan(this.planId);
	}

	get upcomingPlan(): BillingPlan | null {
		// scheduled plan (can be diff from activePlan when downgrading billing cycle)
		let planId, plan;

		const schedulePhases = this.scheduledSubscriptionChanges?.phases;
		if (schedulePhases) {
			planId = schedulePhases[schedulePhases.length - 1]!.items.find(
				(item) => item.type === 'plan',
			)?.id;
		}

		if (planId && planId !== this.activePlan?.id) {
			plan = this.findPlan(planId);
		}

		return plan || null;
	}

	get planOnReactivate(): BillingPlan | null {
		return this.findPlan(this.subscription?.planOnReactivate);
	}

	get finalPlan(): BillingPlan | null {
		// this should be used to show billing cycles and prices
		return this.upcomingPlan || this.activePlan;
	}

	get aiCreditPackItem() {
		// guaranteed to have just one item (confimed by DavidK)
		return this.topUps.find((item) => item.id === BillingAddonType.AI_CREDITS);
	}

	// monthly cost of the purchased extra AI credit packs, in cents (always billed monthly)
	get extraAiCreditsMonthlyPriceCents(): number {
		const quantity = this.aiCreditPackItem?.quantity || 0,
			pricePerPack = this.activePlan?.getAddonPrice(BillingAddonType.AI_CREDITS) ?? 0;
		return quantity * pricePerPack;
	}

	// ---------- SUBSCRIPTION STATUS --------

	get isActive(): boolean {
		return !this.isTrial && !this.isCanceled && !this.isPaymentError;
	}

	get isCanceled(): boolean {
		return this.subscription?.isCanceled && !this.upcomingPlan;
	}

	get isPaymentError(): boolean {
		return this.subscription?.isError || false;
	}

	get isTrial(): boolean {
		return this.subscription?.isTrial || false;
	}

	// The plans that get the Intercom launcher (services/intercom.ts) and an unprompted Book a call (ai-chat `canBookACall`)
	get isFreeOrTrial(): boolean {
		return !!this.activePlan?.isFree || this.isTrial;
	}

	get isActiveTrial(): boolean {
		return this.isTrial && !this.isExpired;
	}

	get isExpiredTrial(): boolean {
		return this.isTrial && this.isExpired && !this.hasPayment;
	}

	get isHigherPlanTrial(): boolean {
		return !!this.flowPromotionsService.activePlanPromotion;
	}

	get isTrialWithoutPayment(): boolean {
		return this.isTrial && !this.isExpired && !this.hasPayment;
	}

	get isActiveAny(): boolean {
		return this.isActive || this.isActiveTrial;
	}

	get isPayingCustomer(): boolean {
		return !this.isTrial && !this.isCanceled && this.isActive && this.hasPayment;
	}

	get isUpcomingBillingCycleChange(): boolean {
		return !!this.upcomingPlan;
	}

	get hasPayment(): boolean {
		return !!this.paymentMethod?.type;
	}

	get activeTimeLeft(): number {
		this.timerService.observe();
		return this.activeUntil - moment().unix();
	}

	get isExpired(): boolean {
		return !!this.activeUntil && this.activeTimeLeft < 0;
	}

	get isPaused(): boolean {
		// only valid for actual subscriptions that are paused
		// for Free, we need to check accountService.isActivityStatePaused (bcos Free have no subscription)
		return !!this.subscription?.isPaused;
	}

	get formattedActiveUntil(): string {
		let time = '';
		if (this.activeUntil) {
			time = getFormattedTime(this.activeUntil, this.currentUserService.model?.timezone);
		}
		return time;
	}

	get formattedFailureMessage(): string | null {
		let failureMessage = this.failureMessage;

		// removing dot from the end of the message so text fits in ()
		if (failureMessage?.charAt(failureMessage.length - 1) === '.') {
			failureMessage = failureMessage.slice(0, -1);
		}

		return failureMessage;
	}

	// ---------- PAYMENT ---------

	get isPayingWithCreditCard(): boolean {
		return this.paymentMethod?.type === BillingPaymentMethod.CC;
	}

	get isPayingWithBankAccount(): boolean {
		return this.paymentMethod?.type === BillingPaymentMethod.BANK_ACC;
	}

	get isPayingViaInvoice(): boolean {
		return this.paymentMethod?.type === BillingPaymentMethod.INVOICE;
	}

	get yearlySavings(): number {
		return this.activePlan?.isLegacy
			? this.savingsFromLegacy
			: this.subscription?.yearlySavings || 0;
	}

	get quarterlySavings(): number {
		return this.subscription.quarterlySavings;
	}

	get formattedYearlySavings(): string {
		return getFormattedPrice(this.yearlySavings);
	}

	get yearlyDiscountText() {
		return htmlSafe(
			DOMPurify.sanitize(
				`<strong>Get 2 months off ${this.activePlan?.isFree ? '' : `(save ${this.formattedYearlySavings}/year)`}</strong> with annual billing.`,
				{
					ALLOWED_TAGS: ['strong'],
				},
			),
		);
	}

	get nextPaymentTs() {
		let nextBillingTs = this.activeUntil;
		if (this.isPayingViaInvoice) {
			// manual payments can show different next bill date
			// e.g. activeUntil can be in 10 days (when invoice must be paid)
			// but next bill can be in 1 year (when invoice is for whole year subscription)
			// https://app.asana.com/0/188486158010832/1205282692387509/f
			const invoicePeriodEnd = this.upcomingInvoiceData?.periodEnd;
			if (invoicePeriodEnd) {
				nextBillingTs = invoicePeriodEnd;
			}
		}
		return nextBillingTs;
	}

	get formattedNextPaymentDate() {
		let time = '';
		if (this.nextPaymentTs) {
			time = getFormattedTime(this.nextPaymentTs, this.currentUserService.model?.timezone);
		}
		return time;
	}

	// ---------- FEATURES & LIMITATIONS ---------

	@tracked boardsCurrent = 0;

	@tracked advancedDataSourcesCurrent = 0;
	@tracked sqlSourcesCurrent = 0;
	@tracked sheetSourcesCurrent = 0;
	@tracked sourcesCurrent = 0;
	@tracked usersCurrent = 0;
	@tracked clientsCurrent = 0;
	@tracked customMetricsCurrent = 0;
	@tracked storageCurrent = 0;
	@tracked aiCreditsCurrent = 0;
	@tracked reportsCurrent = 0;

	get freeSources(): number {
		const freePlanSources = this.finalPlan?.freeSources || 0;
		return freePlanSources + this.sourcesGivenInPA;
	}

	get sourcesPayingFor(): number {
		const sourcesFree = this.freeSources,
			sourcesMinimumBillable = this.sourcesMinimumBillable;
		return Math.max(
			0,
			this.sourcesPayingForWithoutCustomizations,
			this.sourcesFromSubscriptionItemsNonCustomized,
			sourcesMinimumBillable - sourcesFree,
		);
	}

	get sourcesPayingForWithoutCustomizations(): number {
		const sourcesCurrent = this.sourcesCurrent,
			sourcesFree = this.freeSources;
		return Math.max(0, sourcesCurrent - sourcesFree);
	}

	get sourcesGivenInPA(): number {
		return this.customChanges.sources || 0;
	}

	get sourcesMinimumBillable(): number {
		return this.customChanges.minimumBillable || 0;
	}

	get sourcesFromSubscriptionItemsNonCustomized(): number {
		let sources = 0;
		try {
			sources = this.subscription.sourcesItems.find(
				(item) => !item.isCustomization && item.status === SubscriptionStatus.ACTIVE,
			)!.quantity;
		} catch (_e) {
			// skip
		}
		return sources;
	}

	get sourcesMaxCanConnect(): number | null {
		return this.isCanceled ? this.sourcesPayingFor + this.freeSources : null;
	}

	get boardsGivenInPA(): number {
		return this.customChanges.boards || 0;
	}

	get customMetricsGivenInPA(): number {
		return this.customChanges.customMetrics || 0;
	}

	get aiCreditsGivenInPA(): number {
		return this.customChanges.ai_credits || 0;
	}

	get clientsGivenInPA(): number {
		return this.customChanges.clients || 0;
	}

	get spreadsheetDatasetCount(): number {
		return this.mmSourcesService.datasetSpreadsheetSourceCount;
	}

	getFeatureCurrent(featureName: string): number {
		switch (featureName) {
			case BillingFeature.BOARDS:
				return this.boardsCurrent;
			case BillingFeature.SOURCES:
				return this.sourcesCurrent;
			case BillingFeature.ADVANCED_DATA_SOURCES:
				return this.advancedDataSourcesCurrent;
			case BillingFeature.SQL_SOURCES:
				return this.sqlSourcesCurrent;
			case BillingFeature.SHEET_SOURCES:
				return this.sheetSourcesCurrent;
			case BillingFeature.USERS:
				return this.usersCurrent;
			case BillingFeature.CLIENTS:
				return this.clientsCurrent;
			case BillingFeature.CUSTOM_METRICS:
				return this.customMetricsCurrent;
			case BillingFeature.STORAGE:
				return this.storageCurrent;
			case BillingFeature.AI_CREDITS:
				return this.aiCreditsCurrent;
			case BillingFeature.REPORTS_BASIC:
				return this.reportsCurrent;
			default:
				return 0;
		}
	}

	getFeatureValue(featureName: BillingFeature): number | boolean | null {
		return this.features.getValue(featureName);
	}

	getFeatureInfo(featureName: BillingFeature) {
		const featureInfo = this.features.getFeatureInfo(featureName);

		if (featureInfo && this.features.isCountable(featureName)) {
			featureInfo.current = this.getFeatureCurrent(featureName);
		}

		return featureInfo;
	}

	canFeatureBeUpgradedWithPlan(featureName: BillingFeature): boolean {
		const info = this.getFeatureInfo(featureName);
		if (!info) return false;

		if (featureName === BillingFeature.DATA_REFRESH_INTERVAL) {
			return (info.value as number) > 60;
		}
		if (featureName === BillingFeature.CLIENTS) {
			return false;
		}

		const isLimitReached = !this.checkIfCan(featureName);
		const isActive = info.value !== false && info.value !== 0;
		return isLimitReached || !isActive || (info.value !== true && info.value !== null);
	}

	compareFeatureValues(
		featureName: string,
		value1: number | null,
		value2: number | null,
	): boolean {
		return ActiveFeatures.compareFeatureValues(featureName, value1, value2);
	}

	checkIfCan(featureName: BillingFeature, openWarning = false, count = 1): boolean {
		let can: boolean;

		if (this.features.isCountable(featureName)) {
			can = this.checkCountable(featureName, count);
		} else if (
			[BillingFeature.DATA_REFRESH_INTERVAL, BillingFeature.PRIORITY_SYNC].includes(
				featureName,
			)
		) {
			can = true;
		} else {
			can = !!this.features.getValue(featureName);
		}

		if (!can && openWarning) {
			this.handleLimitations(featureName);
		}

		return can;
	}

	private checkCountable(featureName: BillingFeature, count: number): boolean {
		const max = this.features.getValue(featureName) as number | null,
			target = this.getFeatureCurrent(featureName) + count;

		// null value means unlimited!
		let can = max === null || target <= max;

		if (featureName === BillingFeature.SOURCES && this.activePlan && !this.activePlan.isFree) {
			if (this.isCanceled && this.sourcesMaxCanConnect !== null) {
				can = target <= this.sourcesMaxCanConnect;
			} else if (this.isExpiredTrial) {
				can = false;
			}
		}

		return can;
	}

	get whiteLabelingEnabled(): boolean {
		// this is only used to update account.whiteLabelingEnabled prop
		// this can be different from subscription.isAddonEnabled('white_labeling')
		// because it might be scheduled to be canceled at period end
		// use this for webapp whitelabeling features, not for checking if addon buy/remove should be enabled
		return this.features.whiteLabelingEnabled;
	}

	get activityLogEnabled(): boolean {
		return this.features.activityLogEnabled;
	}

	get ssoAllowed(): boolean {
		return this.features.ssoAllowed; // unused since AMA ph2
	}

	get advancedReportsEnabled(): boolean {
		return this.features.advancedReportsEnabled;
	}

	// custom_api_integrations is sent on every plan (false on Sept-2026 plans but premium_2026), so it only unlocks
	// custom integrations together with limited datasets (professional_2024); full datasets always do. When blocked,
	// upsell ADVANCED_DATA_SOURCES: no visible Sept-2026 plan grants custom_api_integrations.
	// mirrors canCreateCustomIntegration in @databox/billing (react-monorepo)
	get canCreateCustomIntegration(): boolean {
		return (
			this.checkIfCan(BillingFeature.ADVANCED_DATA_SOURCES) ||
			(this.checkIfCan(BillingFeature.API_METRIC_BUILDER) &&
				this.checkIfCan(BillingFeature.ADVANCED_DATA_SOURCES_LIMITED))
		);
	}

	get forecastsEnabled(): boolean {
		return this.features.forecastsEnabled;
	}

	get forecastModelingEnabled(): boolean {
		return this.features.forecastModelingEnabled;
	}

	get generativeAIEnabled(): boolean {
		return this.features.generativeAIEnabled;
	}

	get alertsEnabled(): boolean {
		return this.features.alertsEnabled;
	}

	get goalsEnabled(): boolean {
		return this.features.goalsEnabled;
	}

	get goalsOKRsEnabled(): boolean {
		return this.features.goalsOKRsEnabled;
	}

	get reportsBasicEnabled(): boolean {
		return this.features.reportsMax !== 0;
	}

	get workflowIntegrationsEnabled(): boolean {
		return this.features.workflowIntegrationsEnabled;
	}

	get accountTemplatesEnabled(): boolean {
		return this.features.accountTemplatesEnabled;
	}

	get dataPushEnabled(): boolean {
		return this.features.dataPushEnabled;
	}

	get scriptsEnabled(): boolean {
		return this.features.scriptsEnabled;
	}

	get qbEnabled(): boolean {
		return this.features.qbEnabled;
	}

	get visualizationsAdvancedEnabled(): boolean {
		return this.features.visualizationsAdvancedEnabled;
	}

	get instantRefreshEnabled(): boolean {
		return this.features.instantRefreshEnabled;
	}

	get advancedDataSourcesEnabled(): boolean {
		return this.features.advancedDataSourcesEnabled;
	}

	get advancedDataSourcesLimitedEnabled(): boolean {
		return this.features.advancedDataSourcesLimitedEnabled;
	}

	get boardLoopsEnabled(): boolean {
		return this.features.boardLoopsEnabled;
	}

	get sharingEnabled(): boolean {
		return this.features.sharingEnabled;
	}

	get aiCreditsInfoText(): string {
		let text = '';
		const activePlan = this.activePlan;
		if (activePlan) {
			const planValue =
					(this.activePlan.getFeatureValue(BillingFeature.AI_CREDITS) as number) || 0,
				extrasValue = this.features.aiCreditsMax
					? this.features.aiCreditsMax - planValue
					: 0;

			text = `${planValue} plan + ${extrasValue} extra`;

			const aiCreditsResetDate = this.aiCreditsResetDate;
			if (aiCreditsResetDate) {
				text += ` ⋅ resets ${aiCreditsResetDate}`;
			}
		}
		return text;
	}

	get aiCreditsResetDate() {
		const resetTs = this.resetAiCreditsAt;
		return resetTs ? getFormattedTime(resetTs, this.currentUserService.model?.timezone) : '';
	}

	get lockedRoutes() {
		const lockedRoutes: string[] = [];

		if (!this.navigationService.initialRequestsDone) {
			return lockedRoutes;
		}

		if (!this.features.goalsEnabled) {
			lockedRoutes.push('goals', 'goals.index', 'clients.goals', 'mobile.goals');
		}

		if (!this.reportsBasicEnabled) {
			lockedRoutes.push(
				'reports',
				'reports.index',
				'reports.schedules',
				// 'reports.archive', // Allowed
				// 'reports.account-templates', // Can be previewed but not used, will enter locked via accountTemplatesEnabled which can't be true when reportsBasicEnabled is false
				// 'reports.public-templates', // Allowed
				// 'reports.public-templates.template', // Allowed
				// 'reports.view', // Allowed for viewing account templates
				'reports.editor',
			);
		}

		if (!this.features.accountTemplatesEnabled) {
			// If there were existing items route will still load them (not redirect), but the templates can't be used / edited

			lockedRoutes.push('reports.account-templates', 'boards.account-templates');
		}

		if (!this.activityLogEnabled) {
			lockedRoutes.push('settings.logs');
		}

		return lockedRoutes;
	}

	isRouteLocked(route: string) {
		return this.lockedRoutes.includes(route);
	}

	get availableAddonsForActivePlan() {
		// '15-minute sync' is excluded on Premium plan
		return (this.activePlan?.availableAddonsAndTopUps || []).map((addon) => addon.id);
	}

	get purchasedAddonIds(): BillingAddonType[] {
		return this.allAddons.filter((addonId) => {
			if (addonId === BillingAddonType.AI_CREDITS) {
				const creditPack = this.aiCreditPackItem;
				return !!creditPack && creditPack.quantity > 0;
			} else {
				return this.subscription.isAddonEnabled(addonId);
			}
		});
	}

	get areAllAddonsPurchased(): boolean {
		return equal(this.purchasedAddonIds, this.allAddons);
	}

	getAddonInfo(
		addonId: BillingAddonType,
		billingCycle = this.activePlan?.billingCycle,
	): Billing.Addon | undefined {
		const baseAddonInfo = BillingService.addonInfos[addonId];

		if (baseAddonInfo) {
			const addonInfo = Object.assign({}, baseAddonInfo) as Billing.Addon; // the rest is set below
			addonInfo.id = addonId;

			const alwaysMonthly = baseAddonInfo.alwaysMonthly;

			// since Free has no addons as such, we read prices for the next plan
			const planForPrice = this.activePlan?.isFree
				? this.availablePlans.find((p) => !p.isFree)!
				: this.activePlan!;

			const allPlansForPrices = this.availablePlans.filter((p) => {
				return p.id === planForPrice.id || this.checkIsSiblingPlan(planForPrice, p);
			});

			const quantity = this.getAddonQuantity(addonId);

			addonInfo.prices = {} as Billing.Addon['prices'];

			[BillingCycle.MONTHLY, BillingCycle.QUARTERLY, BillingCycle.YEARLY].forEach((cycle) => {
				// forced monthly billing (ai_credits) - read all prices from monthly
				const billingCycleForPrice = alwaysMonthly ? BillingCycle.MONTHLY : cycle;
				const plan = allPlansForPrices.find((p) => p.billingCycle === billingCycleForPrice);

				const price = plan?.getAddonPrice(addonId) || null;

				addonInfo.prices[cycle] = {
					price: price,
					priceFormatted: getFormattedPrice(price || 0),
					totalPriceFormatted: getFormattedPrice((price || 0) * quantity),
					cycleText: plan?.billingCycleTextShort || '',
				};
			});

			// CURRENT PRICES
			if (billingCycle) {
				const billingCycleToUse = alwaysMonthly ? BillingCycle.MONTHLY : billingCycle;
				addonInfo.prices.current = addonInfo.prices[billingCycleToUse];

				// add info about monthly informative price (w/ discounts etc)
				const price = Math.ceil(
					(addonInfo.prices.current.price || 0) /
						this.getMonthsInCycle(billingCycleToUse),
				);
				addonInfo.prices.currentMonthly = {
					price,
					priceFormatted: getFormattedPrice(price),
					cycleText: addonInfo.prices.monthly.cycleText,
				};
			}

			addonInfo.quantity = quantity;
			addonInfo.availableForActivePlan = this.availableAddonsForActivePlan.includes(addonId);
			addonInfo.isPurchased = this.purchasedAddonIds.includes(addonId);
			addonInfo.isCanceled = this.subscription.isAddonCanceled(addonId);
			addonInfo.isTrialing =
				!!this.flowPromotionsService.getActiveTrialAddonPromotion(addonId);
			addonInfo.isAutoEnabled = this.isAddonAutoEnabled(
				addonId,
				addonInfo.isPurchased,
				addonInfo.isCanceled,
			);
			addonInfo.isEnabled =
				addonInfo.isPurchased ||
				addonInfo.isAutoEnabled ||
				addonInfo.isCanceled ||
				addonInfo.isTrialing; // addon will be enabled even if the status is canceled and when the billing cycle ends with the canceled addon, the addon won't be returned from BE
			addonInfo.canAddOnTrial = !!this.activePlan?.getCanAddOnTrial(addonId);

			addonInfo.inactiveInTrial =
				addonInfo.isAutoEnabled &&
				[BillingAddonType.PRIORITY_SUPPORT].includes(addonInfo.id);

			return addonInfo;
		}

		return undefined;
	}

	getAddonQuantity(addonId: BillingAddonType): number {
		return this.invoiceItems.find((item) => item.product_id === addonId)?.quantity || 0;
	}

	getServiceInfo(serviceId: BillingServiceType): Billing.ServiceInfo | undefined {
		const baseServiceInfo = BillingService.serviceInfos[serviceId],
			plansForPrices = this.availablePlans.filter(
				(p) => p.id === this.activePlan!.id || this.isSamePlanCycleChange(p),
			),
			planForMontlyPrice = plansForPrices.find((plan) => plan.isMonthly);

		if (baseServiceInfo && planForMontlyPrice) {
			const serviceInfo = Object.assign({}, baseServiceInfo) as Billing.ServiceInfo; // the rest is set below
			serviceInfo.id = serviceId;
			const price = planForMontlyPrice.getServicePrice(serviceId);
			serviceInfo.price = price ?? null;
			serviceInfo.priceFormatted =
				serviceInfo.price === null ? '' : getFormattedPrice(serviceInfo.price);

			return serviceInfo;
		}

		return undefined;
	}

	checkIfPlanForTrialIsOverriden(planForFeatureType: PlanType | '') {
		return planForFeatureType === PlanType.STARTER;
	}

	getPlanForTrial(feature: BillingFeature | null = null): BillingPlan | undefined {
		const billingCycle = this.flowAnnualPlanService.isEligibleForRegularFlow
				? 'isYearly'
				: 'isMonthly',
			fallback =
				this.availablePlans
					.filter((item) => item.isProfessional || item.isTeamCore)
					.find((item) => item[billingCycle]) || this.availablePlans[0];
		let planForFeature = null;

		if (feature) {
			planForFeature = this.getNextPlanForFeature(
				feature,
				this.availablePlans.filter((item) => item[billingCycle]),
			);

			if (planForFeature?.isStarter) {
				// pre-2026sept billing: Trial plan should be at least professional
				planForFeature = fallback;
			}
		}

		return planForFeature || fallback;
	}

	getPlanFromActiveOrExpiredPromotion(): BillingPlan | undefined {
		const activePromotion = this.flowPromotionsService.activePlanPromotion,
			expiredPromotions = this.flowPromotionsService.expiredPromotions,
			currentBillingCycle = this.activePlan?.billingCycle;

		let plan: BillingPlan | undefined;
		if (currentBillingCycle) {
			const availablePlans = this.availablePlans,
				expiredPromotionWithPlan = expiredPromotions.find(
					(promo) => promo.identifier !== PromotionIdentifier.TRIAL_ADDON,
				);
			if (activePromotion) {
				plan = availablePlans.find((plan) => {
					return (
						activePromotion.productIds?.includes(plan.productId) &&
						plan.billingCycle === currentBillingCycle
					);
				});
			} else if (expiredPromotions.length > 0) {
				plan = availablePlans.find((plan) => {
					return (
						expiredPromotionWithPlan?.productId === plan.productId &&
						plan.billingCycle === currentBillingCycle
					);
				});
			}
		}

		return plan;
	}

	switchToExpiredPromotionPlan(
		trackingOrigin: TrackingOriginForSubscriptionChangePopup,
		targetPlan?: BillingPlan,
	) {
		const plan = targetPlan ?? this.getPlanFromActiveOrExpiredPromotion();
		if (!plan) {
			return;
		}

		this.switchPlan(plan, {
			billingPeriodChangePopup: trackingOrigin,
		});
	}

	getNextPlanForFeature(feature: BillingFeature, plans?: BillingPlan[]): BillingPlan | undefined {
		let planFindFn: (plan: BillingPlan) => boolean;
		if (this.features.isCountable(feature)) {
			const currentValue = this.getFeatureValue(feature) as number;

			planFindFn = (plan) => {
				const planValue = plan.getFeatureValue(feature) as number;
				return (
					planValue === null || // unlimited
					planValue > currentValue
				);
			};
		} else {
			planFindFn = (plan) => {
				return !!plan.getFeatureValue(feature);
			};
		}

		const source = plans || this.availablePlans;

		// Prefer an upgrade from the current plan ("to the right"); plans are no longer
		// strictly linear, so fall back to the full list if no higher plan has the feature.
		const higherPlan = source
			.filter((plan) => this.isPlanUpgrade(plan, false))
			.find(planFindFn);

		return higherPlan || source.find(planFindFn);
	}

	getNextPlanForFeatureWithSameBillingCycle(feature: BillingFeature, allowCustom = false) {
		const activePlan = this.activePlan;

		// for canceled subscriptions, return the active plan if it supports the feature
		if (this.isCanceled && activePlan) {
			if (activePlan.hasFeatureEnabled(feature)) {
				return activePlan;
			}
		}

		let availablePlans = this.availablePlans;
		if (this.canFeatureBeUpgradedWithPlan(feature)) {
			availablePlans = availablePlans.filter(
				(billingPlan) => billingPlan.id !== activePlan?.id,
			);
		}

		const billingCycle = activePlan?.billingCycle || BillingCycle.MONTHLY;
		let plan =
			this.getNextPlanForFeature(
				feature,
				availablePlans.filter((item) => item.billingCycle === billingCycle),
			) || null;

		if (!plan && allowCustom) {
			plan = this.findCustomPlan();
		}

		return plan;
	}

	isAddonCountable(addonId: BillingAddonType) {
		return [
			BillingAddonType.AI_CREDITS,
			BillingAddonType.CLIENT_PACK,
			BillingAddonType.PRIORITY_SYNC,
		].includes(addonId);
	}

	isAddonAutoEnabled(
		addonId: BillingAddonType,
		isPurchased: boolean,
		isCanceled: boolean,
	): boolean {
		if (isPurchased || isCanceled) {
			return false; // Not auto, can be disabled in addon component
		}

		if (this.isAddonCountable(addonId)) {
			// these are countable and as such cannot be autoenabled
			return false;
		}

		return !!this.getFeatureValue(underscore(addonId) as BillingFeature);
	}

	transitionToPlansAndShowReportsGrowthBubble(): void {
		this.navigationService.transitionToRoute('settings.billing.plans').then(() => {
			if (this.tourService.isEligibleForReportsPlansGrowthTour) {
				schedule('afterRender', () => {
					const tour = this.tourService.initTour(TourId.REPORTS__PLAN_PERFORMER);
					if (tour) {
						tour.start();
					}
				});
			}
		});
	}

	// ------- Agency 2026sept downgrade limit handling ------

	@cached
	get agencyClientPackDowngradeLimitsBreached() {
		// checking for CLIENT and SOURCE over the limits for new agency plan
		// assume this was caused by client pack downgrade that expired at end of period
		// we block such agencies and their clients from accessing parts of the app
		if (this.is2026SeptBillingGroup && this.agencyPricing) {
			const limits: Partial<
				Record<BillingFeature, { name: string; current: number; max: number }>
			> = {};
			[BillingFeature.SOURCES, BillingFeature.CLIENTS].forEach((feature) => {
				const featureInfo = this.getFeatureInfo(feature);
				// null max means unlimited; only breached when strictly OVER the limit
				const max = featureInfo?.value as number | null;
				if (featureInfo && max !== null && featureInfo.current! > max) {
					limits[feature] = {
						name: featureInfo.name,
						current: featureInfo.current!,
						max,
					};
				}
			});

			if (Object.keys(limits).length) {
				return limits;
			}
		}

		// // debug
		// return {
		// 	[BillingFeature.SOURCES]: { name: 'Data Sources', current: 4, max: 3 },
		// 	[BillingFeature.CLIENTS]: { name: 'Client accounts', current: 2, max: 1 },
		// };

		return null;
	}

	openAgencyBlockerPopup() {
		const popup = this.popupService.createModal(BillingPopupAgencyBlockerComponent, {
			popupClass: 'popup-type-forbidden-action',
		});
		popup.open();
	}

	openAgencyDowngradePopup() {
		const popup = this.popupService.createModal(BillingPopupAgencyDowngradeComponent, {
			popupClass: 'popup-type-account-billing-cancellation-flow popup-downgrade-limitations',
		});
		popup.open();
	}

	// -------- ALL BILLING OPTIONS -------

	fetchBillingOptions(getAll = false): Promise<unknown> {
		const url = '/billing/available-pricing-plans';
		// const url = '/sources';
		const payload = getAll ? { all: true } : null;
		return this.requestService
			.api<APIData.Billing.AvailablePricingPlansResponse>('GET', url, payload)
			.then((reply) => {
				// All plans (old + 2023 + 2024 + 2025) returned, plus new standard free and agency starter (Mar 2026 changes)
				// reply = window.dbx_mock['GET /d/billing/available-pricing-plans - 1'];

				// pricing 2026 may: analyst and custom added
				// reply = window.dbx_mock['GET /d/billing/available-pricing-plans - 2'];

				// pricing 2026 sept: new plans, new system
				// reply = window.dbx_mock['GET /d/billing/available-pricing-plans - 3'];

				const returnedPlans = reply.plans || [];
				const returnedFeatureSets = reply.featureSets || [];

				this.allPlans = returnedPlans.map((planData) => {
					const featureSetData =
						returnedFeatureSets.find((item) => item.id === planData.featureSet)
							?.features || {};
					return new BillingPlan(planData, featureSetData);
				});

				const aiCreditsFeatureset = returnedFeatureSets.find(
					(f) => f.id === BillingAddonType.AI_CREDITS,
				);
				if (aiCreditsFeatureset) {
					this.aiCreditsInOnePack =
						aiCreditsFeatureset?.features[BillingFeature.AI_CREDITS] || 0;
				}

				const clientPackFeatureset = returnedFeatureSets.find(
					(f) => f.id === BillingAddonType.CLIENT_PACK,
				);
				if (clientPackFeatureset) {
					// the client pack feature set carries these features
					this.featuresInOneClientPack =
						clientPackFeatureset.features as BillingService['featuresInOneClientPack'];
				}
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	validateBillingOptions() {
		const allowEmpty =
			this.accountService.isClient && !this.accountService.hasPartnerResellerId;

		if (allowEmpty) {
			return;
		}

		if (!this.aiCreditsInOnePack) {
			this.loggerService.logError('[Billing] featureSet ai_credits missing');
		}

		if (!this.featuresInOneClientPack[BillingFeature.CLIENTS] && this.agencyPricing) {
			this.loggerService.logError('[Billing] featureSet client_pack missing');
		}
	}

	// plans that are actually available to switch between (plans page)
	// also implements special legacy logic, e.g.:
	// - performer exception for 2024 migration
	// - starter exception for 2025 migration
	@cached
	get availablePlans(): BillingPlan[] {
		const activePlan = this.activePlan;

		if (!activePlan) {
			return [];
		}

		const is2026SeptBillingGroup = this.is2026SeptBillingGroup;

		const isAgencyPricing = this.agencyPricing;

		let plans: BillingPlan[] = [];

		if (is2026SeptBillingGroup) {
			plans = this.allPlans.filter((plan: BillingPlan) => {
				if (plan.id === activePlan.id) {
					return true;
				} else {
					// show agency plans for agency accounts, non-agency plans for non-agency accounts
					const matchesAccountType =
						(isAgencyPricing && plan.isAgency) || (!isAgencyPricing && !plan.isAgency);

					if (!matchesAccountType) {
						// Agency pricing has only ONE plan, not even custom is shown
						return false;
					}

					if (plan.isCustom) {
						return true;
					}

					if (!plan.is2026SeptPlan) {
						return false;
					}

					return true;
				}
			});
		} else {
			const accountCreated = this.accountService.currentSpace?.created || 0;

			plans = this.allPlans.filter((plan: BillingPlan) => {
				// some Performer plans are not legacy, so we need to show them if active plan is also Performer, otherwise hide them
				const isActivePlanPerformer = activePlan.isPerformer && this.isActive;
				if (!isActivePlanPerformer && plan.isPerformer) {
					return false;
				}

				if (plan.is2026SeptPlan && !plan.isFree) {
					return false;
				}

				// show agency plans for agency accounts, non-agency plans for non-agency accounts
				const matchesAccountType =
					(isAgencyPricing && plan.isAgency) || (!isAgencyPricing && !plan.isAgency);

				// if plan has isEnterprise: false, hide it for isAgency (enterprise) spaces (not connected is agencyPricing)
				const matchesEnterpriseFlag = plan.isEnterprise || !this.accountService.isAgency;

				if (!matchesAccountType || !matchesEnterpriseFlag) {
					return false;
				}

				// hide analyst for accounts created before the release date (unless we are on it already)
				if (
					plan.isAnalyst &&
					!activePlan.isAnalyst &&
					accountCreated < this.analystPlanVisibleForCreatedAfter
				) {
					return false;
				}

				let matchesCurrentLegacyStatus: boolean;
				if (activePlan.isLegacy) {
					if (plan.id === activePlan.id) {
						// always show current legacy
						matchesCurrentLegacyStatus = true;
					} else if (plan.isPerformer && plan.is2024Plan) {
						// 2024 performer plans are always shown if user is on a performer plan
						matchesCurrentLegacyStatus = true;
					} else if (
						plan.planType === activePlan.planType &&
						plan.billingCycle !== activePlan.billingCycle
					) {
						// for active legacy, we show plan of the same type when:
						// A) allowCycleSwitch=true and its also legacy of the same batch
						// B) allowCycleSwitch=false and its nonlegacy from the succeedBy batch
						if (
							activePlan.allowCycleSwitch &&
							plan.isLegacy &&
							this.checkIsSiblingPlan(activePlan, plan)
						) {
							matchesCurrentLegacyStatus = true;
						} else if (!activePlan.allowCycleSwitch && !plan.isLegacy) {
							const successorPlans =
								this.getSuccessorPlansOnAllBillingCycles(activePlan);
							matchesCurrentLegacyStatus = !!successorPlans.find(
								(p) => p.id === plan.id,
							);
						} else {
							matchesCurrentLegacyStatus = false;
						}
					} else if (!plan.isLegacy) {
						if (plan.isCustom) {
							return true;
						} else {
							// new plans of different cycle are ok
							matchesCurrentLegacyStatus = plan.planType !== activePlan.planType;
						}
					} else {
						matchesCurrentLegacyStatus = false;
					}
				} else {
					matchesCurrentLegacyStatus = !plan.isLegacy;
				}

				if (!matchesCurrentLegacyStatus) {
					return false;
				}

				return true;
			});
		}

		// sort plans in "upgrades" order
		const sortByPlanType = (a: BillingPlan, b: BillingPlan) => {
			return (
				BillingPlan.planTypes.indexOf(a.planType) -
				BillingPlan.planTypes.indexOf(b.planType)
			);
		};

		return plans.sort(sortByPlanType);
	}

	findPlan(planId: string): BillingPlan | null {
		return this.allPlans.find((item) => item.id === planId) || null;
	}

	findFreePlan(): BillingPlan | null {
		return this.allPlans.find((p) => p.isFree && !p.isLegacy) || null;
	}

	findCustomPlan(): BillingPlan | null {
		return this.allPlans.find((p) => p.isCustom) || null;
	}

	// --------- SUBSCRIPTION CHANGES ---------
	get notMigratedAndEligibleForMigration() {
		return this.eligibleForMigration;
	}

	get isAgencyAdmin() {
		return (
			this.currentUserService.isAdmin &&
			!this.accountService.isClient &&
			!this.currentUserService.isAgencyUserInClientSpace
		);
	}

	checkForPermissions(feature?: BillingFeature): boolean {
		// checks if current user has permissions to change any billing stuff
		// if not, shows info why not. if yes, returns true

		if (this.isAgencyAdmin) {
			return true;
		} else {
			if (feature) {
				this.handleLimitations(feature);
				return false;
			} else {
				let infolayerText;
				if (this.accountService.isClient) {
					const agencyAdmin = this.mmUsersService.agencyAdminRecordForClientSpace;
					infolayerText = 'Contact your organization for more information';
					if (agencyAdmin) {
						infolayerText += ': ' + agencyAdmin.name + ' (' + agencyAdmin.email + ')';
					}
				} else {
					infolayerText = 'Contact your admin for more information.';
				}

				if (infolayerText) {
					this.infolayerService.error(infolayerText);
				}
				return false;
			}
		}
	}

	isCurrentPlan(plan: BillingPlan): boolean {
		return this.activePlan?.id === plan.id;
	}

	isPlanSameLevel(plan: BillingPlan, checkCycle = true): boolean {
		let isSameLevel = false;

		if (this.activePlan) {
			isSameLevel =
				this.activePlan.planType === plan.planType &&
				(checkCycle ? this.activePlan.monthsInCycle === plan.monthsInCycle : true);
		}

		return isSameLevel;
	}

	isPlanUpgrade(plan: BillingPlan, checkCycle = true): boolean {
		/*
			Checks if given plan is an upgrade from current plan.
			When checkCycle is true, we treat a cycle change as upgrade / downgrade
		*/

		let isUpgrade = false;

		if (this.activePlan) {
			const planTypes = BillingPlan.planTypes,
				isSameLevel = this.isPlanSameLevel(plan, checkCycle),
				currentPlanType = planTypes.indexOf(this.activePlan.planType),
				targetPlanType = planTypes.indexOf(plan.planType);

			if (currentPlanType === -1) {
				// if not one of the listed, is some custom = treat as downgrade
				isUpgrade = false;
			} else if (targetPlanType === -1) {
				// treat unknown / custom as upgrade
				isUpgrade = true;
			} else if (isSameLevel) {
				return false;
			} else if (currentPlanType !== targetPlanType) {
				// different plan type, the highest wins
				isUpgrade = targetPlanType > currentPlanType;
			} else {
				// same plan type, check cycle
				isUpgrade = plan.monthsInCycle > (this.activePlan?.monthsInCycle || 0);
			}
		}

		return isUpgrade;
	}

	isPlanDowngrade(plan: BillingPlan, checkCycle = true): boolean {
		let isDowngrade = false;

		if (this.activePlan) {
			const isSameLevel = this.isPlanSameLevel(plan, checkCycle);

			if (!isSameLevel) {
				isDowngrade = !this.isPlanUpgrade(plan, checkCycle);
			}
		}

		return isDowngrade;
	}

	isSamePlanCycleChange(plan: BillingPlan): boolean {
		const activePlan = this.activePlan;
		return !!activePlan && this.checkIsSiblingPlan(activePlan, plan);
	}

	getSuccessorPlansOnAllBillingCycles(plan: BillingPlan): BillingPlan[] {
		const succeedBy = plan.succeedBy;
		if (succeedBy) {
			const successor = this.findPlan(succeedBy);
			if (successor) {
				const plansOfSuccessorType = this.allPlans.filter(
					(p) => p.planType === successor.planType,
				);
				return plansOfSuccessorType.filter((p) => this.checkIsSiblingPlan(p, successor));
			}
		}
		return [];
	}

	private checkIsSiblingPlan(plan1: BillingPlan, plan2: BillingPlan): boolean {
		// same type, diff billing cycle
		if (
			plan1.id !== plan2.id &&
			plan1.planType === plan2.planType &&
			plan1.featureSet === plan2.featureSet
		) {
			// checking if they are succeeded by siblings
			if (!plan1.succeedBy && !plan2.succeedBy) {
				return true;
			} else {
				const plan1Successor = plan1.succeedBy ? this.findPlan(plan1.succeedBy) : null;
				const plan2Successor = plan2.succeedBy ? this.findPlan(plan2.succeedBy) : null;
				if (plan1Successor && plan2Successor) {
					return this.checkIsSiblingPlan(plan1Successor, plan2Successor);
				} else {
					return false;
				}
			}
		} else {
			return false;
		}
	}

	keepDiscountAndCustomizationsOnPlan(plan: BillingPlan): boolean {
		return (
			this.isCurrentPlan(plan) || this.isSamePlanCycleChange(plan) || this.isPlanUpgrade(plan)
		);
	}

	checkForDowngradeLimits(plan: BillingPlan): boolean {
		// checks if we can downgrade active plan to the given plan
		// without deleting anything. if can, returns true
		const isOverLimit = (
			[
				BillingFeature.BOARDS,
				BillingFeature.SOURCES,
				BillingFeature.USERS,
				BillingFeature.CLIENTS,
				BillingFeature.CUSTOM_METRICS,
				BillingFeature.ADVANCED_DATA_SOURCES,
				BillingFeature.SHEET_SOURCES,
				BillingFeature.SQL_SOURCES,
				BillingFeature.REPORTS_BASIC,
			] as CountableFeatureName[]
		).some((featureName) => {
			return !this.checkForDowngradeLimitsForFeature(plan, featureName);
		});
		return !isOverLimit;
	}

	checkForDowngradeLimitsForFeature(
		plan: BillingPlan,
		featureName: CountableFeatureName,
	): boolean {
		// checks if we can downgrade active plan to the given plan on the given feature
		// without deleting anything. if can, returns true

		if (featureName === BillingFeature.SOURCES && plan.isExtraSourcesAllowedOnPlan) {
			return true;
		}

		const currentCount = this.getFeatureCurrent(featureName);
		let maxLimitation = plan.getFeatureValue(featureName);

		if (
			featureName === BillingFeature.ADVANCED_DATA_SOURCES ||
			featureName === BillingFeature.SQL_SOURCES ||
			featureName === BillingFeature.SHEET_SOURCES
		) {
			maxLimitation = maxLimitation === true ? null : 0;
		}

		return maxLimitation === null || currentCount <= (maxLimitation as number);
	}

	doPlanSwitchEndpoint(data: ChangePlanData): Promise<APIData.Billing.PlanSwitchResponse> {
		this.toggleFullscreenProcessingLayer();

		const payload: APIData.Billing.ChangePlanRequest = {
			planId: data.planId,
		};

		if (typeof data.trialing !== 'undefined') {
			payload.trialing = data.trialing;
		}

		if (typeof data.extras !== 'undefined') {
			payload.extras = data.extras;
		}

		if (typeof data.promotionExtras !== 'undefined') {
			payload.promotionExtras = data.promotionExtras;
		}

		if (typeof data.promotionId !== 'undefined') {
			payload.promotionId = data.promotionId;
		}

		const state: ChangePlanStateOptions = {};
		Object.keys(data).forEach((key) => {
			const dataKey = key as keyof ChangePlanStateOptions;
			if (BillingCallbackStateOptionKeys.includes(dataKey)) {
				state[dataKey] = data[dataKey];
			}
		});

		if (Object.keys(state).length) {
			payload.state = btoa(JSON.stringify(state)); // base64 encode
		}

		return this.requestService
			.api<APIData.Billing.PlanSwitchResponse>('POST', '/billing/plan', payload)
			.then(
				(reply) => {
					if (reply.checkoutUrl) {
						this.redirectToCheckout(reply.checkoutUrl);
						return new Promise<never>(() => {
							/* keep waiting */
						});
					} else {
						this._handleBillingEndpointResponseData(reply);
						this.toggleFullscreenProcessingLayer(true);
					}
					return reply;
				},
				(errorData: RequestError) => {
					const logData = {
						plan: payload.planId,
						message: (errorData.errors || [])[0]?.message || '',
					};

					this.loggerService.logError(
						'Billing plan save error',
						logData,
						Severity.WARNING,
					);

					this.toggleFullscreenProcessingLayer(true);

					throw errorData;
				},
			);
	}

	switchPlanWithoutConfirm(
		plan: BillingPlan,
		extraItems?: ExtraItem[],
		trackingOrigin?: TrackingOriginForSubscriptionChangePopup,
		afterFlags?: ChangePlanStateOptions,
	): Promise<void> {
		// don't allow invoice users to switch to shorter billing cycle
		if (
			this.isPayingViaInvoice &&
			this.finalPlan &&
			!this.finalPlan.isFree &&
			plan.monthsInCycle < this.finalPlan.monthsInCycle
		) {
			this.openInvoiceDowngradePopup();
			return Promise.reject();
		}

		const layer = this.infolayerService.loading('Saving payment plan');

		const data: ChangePlanData = Object.assign(afterFlags || {}, {
			planId: plan.id,
			trialing: false,
			openGrowthPaidTrialPopup: !this.hasPayment,
		});

		if (extraItems?.length) {
			const nonFreeAddonsOrServices: ExtraItem[] = [],
				freeAddonsOrServices: ExtraItem[] = [];
			extraItems.forEach((extraItem) => {
				if (extraItem.isFree) {
					freeAddonsOrServices.push(extraItem);
				} else {
					nonFreeAddonsOrServices.push(extraItem);
				}
			});

			if (nonFreeAddonsOrServices.length) {
				data.extras = nonFreeAddonsOrServices.map((extraItem) => ({
					productPriceId: extraItem.priceId,
					quantity: 1,
				}));
			}

			if (freeAddonsOrServices.length) {
				data.promotionExtras = freeAddonsOrServices.map((extraItem) => ({
					productPriceId: extraItem.priceId,
					quantity: 1,
				}));
			}
		}

		this.triggerChangeBillingPeriodEvent(plan, trackingOrigin, !!extraItems);
		this.triggerPlanChangeEvent(plan, null, trackingOrigin, !!extraItems);

		return this.doPlanSwitchEndpoint(data).then(
			() => {
				layer.ok('Payment plan changed successfully!');
				this.resetRequestedInvoices(); // maybe we have a new invoice pending?
				this.navigationService.transitionToRoute('settings.billing.index');
			},
			(errorData: RequestError) => {
				let errorMsg = 'Error changing payment plan.';
				if (errorData.errors) {
					errorMsg = errorData.errors[0]!.message;
				}
				layer.error(errorMsg);

				throw errorData;
			},
		);
	}

	switchPlan(
		plan: BillingPlan,
		trackingOrigin?: {
			billingPeriodChangePopup?: TrackingOriginForSubscriptionChangePopup;
			popupTrackingOriginTypeForTrialPopupOpened?: TrackingOriginTypeForTrialPopupOpened;
		},
		afterFlags?: ChangePlanStateOptions,
		onPlanSwitched?: () => void,
	): void {
		const isDowngrade = this.isPlanDowngrade(plan),
			isCycleChange = this.isSamePlanCycleChange(plan);

		if (isDowngrade && !isCycleChange && this.handleCommitmentRestriction()) {
			return;
		}

		this.flowCancellationService.trackingOriginForBillingPeriodChangePopup =
			trackingOrigin?.billingPeriodChangePopup;

		const continueFn = () => {
			const affirmativeFunc = (
				plan: BillingPlan,
				addonObjects?: ExtraItem[],
				serviceObjects?: ExtraItem[],
				trackingOrigin?: TrackingOriginForSubscriptionChangePopup,
				afterFlagsFromPopup?: ChangePlanStateOptions,
			) => {
				const currentPlan = this.activePlan;

				if (currentPlan?.id !== plan.id) {
					const allAfterFlags = Object.assign(afterFlags || {}, afterFlagsFromPopup),
						extraItems = [...(addonObjects || []), ...(serviceObjects || [])];

					onPlanSwitched?.();
					return this.switchPlanWithoutConfirm(
						plan,
						extraItems,
						trackingOrigin,
						allAfterFlags,
					);
				} else if (addonObjects?.length || serviceObjects?.length) {
					const promises: Promise<void>[] = [];
					addonObjects
						?.filter((addonObject) => !addonObject.isFree)
						.map((addonObject) => addonObject.id)
						.forEach((addonId) => {
							promises.push(this.addAddon(addonId as BillingAddonType));
						});

					serviceObjects
						?.filter((serviceObject) => !serviceObject.isFree)
						.map((serviceObject) => serviceObject.id)
						.forEach((serviceId) => {
							promises.push(this.addService(serviceId));
						});

					return Promise.all(promises);
				} else {
					return Promise.reject({
						message: "You can't switch to the same plan.",
					});
				}
			};

			if (plan.isFree) {
				// this.flowCancellationService.trackingOriginForCancellationFlowStarted = 'downgrade';
				this.flowCancellationService.downgradeToFree();
			} else {
				this.openConfirmPopup({
					model: plan,
					onConfirm: affirmativeFunc,
					trackingOrigin: trackingOrigin?.billingPeriodChangePopup,
				});
			}
		};

		if (isDowngrade || plan.isFree) {
			void this.userSettingsService.save(
				UserSettingsKey.BEST_VALUE_PLAN_BANNER_COOLDOWN_TIMESTAMP,
				moment().unix(),
			);
		}

		if (
			isDowngrade &&
			!plan.isFree &&
			!this.isCurrentPlan(plan) &&
			!isCycleChange &&
			!this.checkForDowngradeLimits(plan)
		) {
			this.flowCancellationService.trackingOriginForCancellationFlowStarted = 'downgrade';
			const popup = this.flowCancellationService.prepareDowngradeLimitationsPopup();
			popup.on('confirm', this, continueFn);
			popup.open({ targetPlan: plan });
			return;
		}

		continueFn();
	}

	doFirstTrialRequest(
		origin: string | null,
		trialFeatures: string[],
		plan: BillingPlan,
		trialObjective?: string,
		skipPopup = false,
	): Promise<void> {
		const payload = {
			plan: plan.id,
			originUrl: window.location.href,
			origin,
			trialFeatures,
			trialObjective: trialObjective || '/',
		};

		return this.requestService.api('POST', '/account/trial', payload).then(() => {
			if (!skipPopup) {
				this.openTrialActivatedPopup(plan);
			}
		});
	}

	@tracked requestTrialInProgress = false;

	requestTrial(
		fromFeature: BillingFeature | null = null,
		plan?: BillingPlan | null,
		trackingOrigin?: TrackingOriginTypeForTrialPopupOpened,
		skipPopup = false,
	): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.requestTrialInProgress) {
				reject();
				return;
			}

			const trialPlan = plan || this.getPlanForTrial(fromFeature);

			if (trialPlan && this.hadTrial) {
				// 2nd+ trial - different endpoint

				const continueFn = () => {
					const layer = this.infolayerService.loading('Changing trial plan...');

					const data: APIData.Billing.ChangePlanRequest = {
						planId: trialPlan.id,
						trialing: true,
					};

					this.requestTrialInProgress = true;

					this.triggerChangeBillingPeriodEvent(trialPlan, trackingOrigin);
					this.triggerPlanChangeEvent(trialPlan, fromFeature, trackingOrigin);

					this.doPlanSwitchEndpoint(data)
						.then(
							() => {
								const timeLeft = moment
									.duration(this.activeTimeLeft, 'second')
									.humanize();
								let msg = `Successfully changed trial plan to ${trialPlan.name}.`;
								msg += ` You have ${timeLeft} left on your trial.`;
								layer.ok(msg);
								resolve();
							},
							(errorData: RequestError) => {
								layer.close();
								throw errorData;
							},
						)
						.catch(() => {
							this.requestService.showAllValidationErrors.bind(this.requestService);
							reject();
						})
						.finally(() => {
							this.requestTrialInProgress = false;
						});
				};

				if (
					this.isPlanDowngrade(trialPlan) &&
					!this.isSamePlanCycleChange(trialPlan) &&
					!this.checkForDowngradeLimits(trialPlan)
				) {
					const popup = this.flowCancellationService.prepareDowngradeLimitationsPopup();
					popup.on('confirm', this, continueFn);
					popup.open({ targetPlan: trialPlan });
				} else {
					continueFn();
				}
			} else {
				let origin = trackingOrigin || null;
				if (!!fromFeature && !origin) {
					origin = this.getOriginByFeatureForTrialPopupOpened(fromFeature);
				}

				if (
					trialPlan &&
					origin &&
					[
						'onboarding',
						'pricing_page',
						'billing_page',
						'top_banner',
						'feature_limitation_reports',
					].includes(origin)
				) {
					const trialFeature =
						origin === 'onboarding'
							? 'Create custom metrics with Query Builder'
							: 'Explore Databox';

					this.requestTrialInProgress = true;
					this.doFirstTrialRequest(
						origin,
						[trialFeature],
						trialPlan,
						undefined,
						skipPopup,
					)
						.catch(
							this.requestService.handleGenericThrownError.bind(this.requestService),
						)
						.finally(() => {
							this.requestTrialInProgress = false;
							resolve();
						});
				} else {
					this.openFreeTrialRequestPopup(fromFeature, origin);
					resolve();
				}
			}
		});
	}

	changePayment(): void {
		this.toggleFullscreenProcessingLayer();

		const openServicesAndAddonsPopup = !this.hasPayment && !this.areAllAddonsPurchased,
			payload: APIData.Billing.ChangePayment = {},
			state: ChangePlanStateOptions = {
				openServicesAndAddonsPopup,
			};
		payload.state = btoa(JSON.stringify(state));

		this.requestService
			.api<APIData.Billing.PaymentMethodResponse>('POST', '/billing/payment-method', payload)
			.then(
				(reply) => {
					// const state: ChangePlanStateOptions = { openServicesAndAddonsPopup: true };
					// const encodedState = btoa(JSON.stringify(state)); // base64 encode
					// reply = { checkoutUrl: `/settings/billing/callback?success=true&state=${encodedState}` };
					if (reply.checkoutUrl) {
						this.redirectToCheckout(reply.checkoutUrl);
					}
				},
				(errorData: RequestError) => {
					const logData = { message: '' };

					let errorMsg = 'We have encountered an error, please try again later';
					if (errorData.errors) {
						errorMsg = errorData.errors[0]!.message;
						logData.message = errorMsg;
					}
					this.infolayerService.error(errorMsg);

					this.loggerService.logError(
						'Getting payment method checkoutUrl error',
						logData,
						Severity.CRITICAL,
					);

					this.toggleFullscreenProcessingLayer(true);
				},
			);
	}

	addAddon(addonId: BillingAddonType, quantity = 1): Promise<void> {
		const payload = {
			productId: addonId,
			quantity,
		};

		this.toggleFullscreenProcessingLayer(this.isTrial);
		return this.requestService
			.api<APIData.Billing.EndpointResponse>('POST', '/billing/extra', payload, {
				timeout: 2 * 60 * 1000,
			})
			.then(
				(reply) => {
					this._handleBillingEndpointResponseData(reply);
					this.toggleFullscreenProcessingLayer(true);
				},
				(errorData: RequestError) => {
					this.toggleFullscreenProcessingLayer(true);
					throw errorData;
				},
			);
	}

	removeAddon(addonId: BillingAddonType): Promise<void> {
		const payload = {
			productId: addonId,
			// quantity: 1,
		};

		return this.requestService
			.api<APIData.Billing.EndpointResponse>('DELETE', '/billing/extra', payload)
			.then((reply) => {
				this._handleBillingEndpointResponseData(reply);
			});
	}

	addService(serviceId: string): Promise<void> {
		const payload = {
			productId: serviceId,
			// quantity: 1,
		};

		this.toggleFullscreenProcessingLayer();
		return this.requestService
			.api<APIData.Billing.EndpointResponse>('POST', '/billing/product/buy', payload)
			.then(
				(reply) => {
					this._handleBillingEndpointResponseData(reply);
					this.toggleFullscreenProcessingLayer(true);
				},
				(errorData: RequestError) => {
					this.toggleFullscreenProcessingLayer(true);
					throw errorData;
				},
			);
	}

	cancelSubscription(): Promise<unknown> {
		this.analyticsService.event(TrackingEvent.CANCEL_SUBSCRIPTION);
		return this.requestService
			.api<APIData.Billing.EndpointResponse>('DELETE', '/billing/payment-method')
			.then((reply) => {
				this._handleBillingEndpointResponseData(reply);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	_handleBillingEndpointResponseData(data: APIData.Billing.EndpointResponse): void {
		if (data) {
			const billingInfo = data.billingInfo || data.account?.billingInfo;
			if (billingInfo) {
				if (!this.isProviderProcessing && billingInfo.isProviderProcessing) {
					// show infolayer when processing, and hide it when pusher update comes where it's not processing anymore
					const layer = this.infolayerService.loading('Updating your subscription...'),
						hideLayerFn = () => {
							if (!this.isProviderProcessing) {
								layer.ok('Subscription updated!');

								removeObserver(this, 'isProviderProcessing', this, hideLayerFn);
							}
						};
					// eslint-disable-next-line ember/no-observers
					addObserver(this, 'isProviderProcessing', this, hideLayerFn);
				}

				this.updateBillingInfo(billingInfo);
				delete data.billingInfo;
				delete data.account;
			}
			// if (Object.keys(data).length) {
			// 	this.storeService.pushPayload(data);
			// }
		}
	}

	private fullscreenProcessingLayer: PopupModal | null = null;
	private toggleFullscreenProcessingLayer(isHide = false): void {
		if (isHide) {
			const popup = this.fullscreenProcessingLayer;
			if (popup) {
				void popup.close();
				this.fullscreenProcessingLayer = null;
			}
		} else {
			if (!this.fullscreenProcessingLayer) {
				const popup = this.navigationService.createFullscreenLoaderModal();
				popup.open({ text: 'Processing...' });
				this.fullscreenProcessingLayer = popup;
			}
		}
	}

	private redirectToCheckout(checkoutUrl: string) {
		this.analyticsService.event(TrackingEvent.PAYMENT_DETAILS_POPUP_SHOWN);
		redirect(checkoutUrl);
	}

	// ---------- INVOICES -------

	get invoiceItems() {
		return this.upcomingInvoiceData?.items || [];
	}

	_invoicesRequestPromise: Promise<void> | null = null;
	_invoices: Billing.Invoice[] = []; // don't access this directly, use getInvoices instead

	getInvoices(getAll = false): Promise<Billing.Invoice[]> {
		// when requesting all, request each time
		// when requesting just 5 (for billing overview page), only once

		const requestFn = (count?: number): Promise<Billing.Invoice[]> => {
			const params = count ? { count } : null;
			return this.requestService
				.api<APIData.Billing.PaymentHistoryResponse>(
					'GET',
					'/billing/payment-history',
					params,
				)
				.then((reply) => {
					// reply = window.dbx_mock['GET /d/billing/payment-history'];

					const parsedReply = reply.map(
						(item: APIData.Billing.PaymentHistoryResponseItem) => {
							return {
								id: item.id,
								date: item.date,
								status: item.status,
								amountDue: item.amount_due,
								receiptNumber: item.receipt_number,
								dateFormatted: getFormattedTime(
									item.date,
									this.currentUserService.model?.timezone,
									'MMMM D, YYYY',
								),
								amountDueFormatted: getFormattedPrice(item.amount_due, 2),
								pdfLink: item.host_pdf_url,
							};
						},
					);

					return parsedReply;
				});
		};

		if (getAll) {
			return requestFn();
		} else {
			if (!this._invoicesRequestPromise) {
				this._invoicesRequestPromise = requestFn(1).then(
					(reply: Billing.Invoice[]) => {
						this._invoices = reply || [];
						this._invoicesRequestPromise = Promise.resolve();
					},
					() => {
						this._invoicesRequestPromise = null; // so we try again
					},
				);
			}

			return this._invoicesRequestPromise.then(() => {
				return this._invoices;
			});
		}
	}

	updateBillingEmail(email: string): Promise<void> {
		return this.requestService
			.api<APIData.Billing.EndpointResponse>('POST', '/billing/email', { email })
			.then((reply) => {
				this.billingEmail = email;
				this._handleBillingEndpointResponseData(reply);
			});
	}

	resetRequestedInvoices(): void {
		// so we can fetch invoices again
		// this is being called after changing a plan and when getting fresh billingInfo over socket
		this._invoicesRequestPromise = null;
	}

	parseInvoiceData(invoiceData: RawInvoiceData | null, overwriteName = true): ParsedInvoiceData {
		const billingCycle = invoiceData?.billingCycle || BillingCycle.MONTHLY,
			monthsInCycle = this.getMonthsInCycle(billingCycle);

		const parsedInvoice: ParsedInvoiceData = {
			total: invoiceData?.total || 0,
			items: invoiceData?.items || [],
			billingCycle: billingCycle || '',
			billingCycleDiscount: this.billingCycleDiscounts[billingCycle],
			monthsInCycle,
			description: '',
			periodStart: invoiceData?.periodStart || 0,
			periodEnd: invoiceData?.periodEnd || 0,
			totalFormatted: '',
			totalMonthlyFormatted: '',
		};

		parsedInvoice.totalFormatted = getFormattedPrice(parsedInvoice.total, 2);
		parsedInvoice.totalMonthlyFormatted = getFormattedMonthlyPrice(
			parsedInvoice.total,
			2,
			monthsInCycle,
		);

		parsedInvoice.items.forEach((item) => {
			item.formattedPrice = getFormattedMonthlyPrice(item.price, 2, monthsInCycle);
			item.formattedPricePerItem = getFormattedMonthlyPrice(
				item.pricePerItem,
				2,
				monthsInCycle,
			);
			if (item.type === 'plan' && overwriteName) {
				item.name = 'Base plan'; // overwrite
			} else if (
				item.type === PlanExtrasType.EXTENSION ||
				(item.type === PlanExtrasType.ADD_ON && item.quantity > 1)
			) {
				const itemText = [
					BillingAddonType.AI_CREDITS,
					BillingAddonType.CLIENT_PACK,
				].includes(item.product_id as BillingAddonType)
					? 'pack'
					: 'connection';
				item.shownQuantity = item.quantity;
				item.description = `(${item.formattedPricePerItem} per ${itemText})`;
			}

			if (item.type !== 'discount' && parsedInvoice.billingCycleDiscount) {
				item.priceOff = `-${parsedInvoice.billingCycleDiscount}% off`;
			}

			if (
				item.type === 'discount' &&
				item.name.toLowerCase().includes('grandfather') &&
				!this.grandFathered
			) {
				item.name = 'discount';
			}

			item.name = capitalize(item.name);
		});

		return parsedInvoice;
	}

	// ---------- BILLING-CONNECTED NOTIFICATIONS / STATUSES -------

	get isCardExpirationThisMonth(): boolean {
		return (
			this.isPayingWithCreditCard &&
			!!this.paymentMethod?.expYear &&
			`${this.paymentMethod.expMonth}/${this.paymentMethod.expYear}` ===
				moment().format('MM/YYYY')
		);
	}

	get showAppNotification(): boolean {
		return this.isPaymentError || this.isPaused || (this.isCanceled && this.isExpired);
	}

	get showPaymentErrorWarning(): boolean {
		return this.failedPaymentCnt > 3 && this.isPaymentError;
	}

	get hadTrial(): boolean {
		return !!this.trialRequestCnt;
	}

	get canRequestTrial(): boolean {
		// Can request trial by regular flow, different from automated trial promotion for paying customers

		if (this.accountService.isClient) {
			return false;
		} else if (this.isHigherPlanTrial) {
			return false;
		} else if (this.isPayingCustomer) {
			return false;
		} else {
			return this.hadTrial && this.isActiveTrial; // switching plan while still on trial
		}
	}

	get canRequestTrialAddon(): boolean {
		return !this.accountService.isClient;
	}

	get showYearlyUpgradeNotification(): boolean {
		const isAdmin = this.currentUserService.isAdmin,
			activePlan = this.activePlan,
			becamePayingTS = this.becamePaying || 0,
			payingForTwoMonths =
				moment.unix(becamePayingTS).add(2, 'months').unix() >= moment().unix();

		return (
			isAdmin &&
			!!activePlan &&
			activePlan.isMonthly &&
			!activePlan.isFree &&
			!activePlan.isVIP &&
			!payingForTwoMonths &&
			!this.isTrial &&
			this.yearlySavings > 0
		);
	}

	get showDataSyncUpgrade(): boolean {
		return this.features.dataRefreshInterval >= 24 * 60; // 1440 daily ali več
	}

	get showUnverifiedAgencyWarning(): boolean {
		return (
			!this.agencyPricing &&
			this.accountService.isAgency &&
			!this.userSettingsService.hasDismissedUnverifiedAgencyWarning
		);
	}

	// ---------- CHECKOUT HANDLER --------

	handleCheckoutCallback(state: ChangePlanStateOptions) {
		if (state.openServicesAndAddonsPopup) {
			this.openServicesAndAddonsPopup();
		} else if (state.openGrowthPaidTrialPopup) {
			this.checkToOpenGrowthPaidTrialPopup();
		}
	}

	// ---------- HELPER METHODS ---------

	getMonthsInCycle(billingCycle: BillingCycle) {
		return billingCycle === BillingCycle.YEARLY
			? 12
			: billingCycle === BillingCycle.QUARTERLY
				? 3
				: 1;
	}

	onPlanChange(_1: BillingPlan | null, newPlan: BillingPlan): void {
		this.features.updateFromPlan(newPlan);

		// reset limitations so we fetch all new
		this.mmMetricsService.purgeLimitationsCache();
	}

	updateBillingInfo(data: BillingInfoData): void {
		if (typeof data.agencyPricing !== 'undefined') {
			this.agencyPricing = data.agencyPricing;
		}
		if (typeof data.activeUntil !== 'undefined') {
			this.activeUntil = data.activeUntil;
		}
		if (typeof data.becamePaying !== 'undefined') {
			this.becamePaying = data.becamePaying;
		}
		if (typeof data.customerId !== 'undefined') {
			this.customerId = data.customerId;
		}
		if (typeof data.billingEmail !== 'undefined') {
			this.billingEmail = data.billingEmail;
		}
		if (typeof data.failedPaymentCnt !== 'undefined') {
			this.failedPaymentCnt = data.failedPaymentCnt;
		}
		if (typeof data.trialCount !== 'undefined') {
			this.trialCount = data.trialCount;
		}
		if (typeof data.trialRequestCnt !== 'undefined') {
			this.trialRequestCnt = data.trialRequestCnt;
		}
		if (typeof data.discount !== 'undefined') {
			this.monthlyDiscount = data.discount || 0;
		}
		if (typeof data.paymentMethod !== 'undefined') {
			this.paymentMethod = data.paymentMethod;
		}
		if (typeof data.isProviderProcessing !== 'undefined') {
			this.isProviderProcessing = data.isProviderProcessing;
		}
		if (typeof data.grandFathered !== 'undefined') {
			// Must be set before parseInvoiceData
			this.grandFathered = data.grandFathered;
		}
		if (typeof data.upcomingInvoice !== 'undefined') {
			this.upcomingInvoiceData = this.parseInvoiceData(data.upcomingInvoice);
		}
		if (typeof data.custom !== 'undefined') {
			this.customChanges = data.custom || {};
		}
		if (typeof data.failureMessage !== 'undefined') {
			this.failureMessage = data.failureMessage;
		}
		if (typeof data.commitment !== 'undefined') {
			this.commitment = data.commitment;
		}

		if (typeof data.eligibleForMigration !== 'undefined') {
			this.eligibleForMigration = data.eligibleForMigration;
		}
		if (typeof data.lastPayingPlan !== 'undefined') {
			this.lastPayingPlan = data.lastPayingPlan;
		}
		if (typeof data.nextSubscription !== 'undefined') {
			this.nextSubscription = this.parseInvoiceData(data.nextSubscription, false);
		}
		if (typeof data.previousSubscription !== 'undefined') {
			this.previousSubscription = this.parseInvoiceData(data.previousSubscription, false);
		}
		if (typeof data.savingsFromLegacy !== 'undefined') {
			this.savingsFromLegacy = data.savingsFromLegacy || 0;
		}
		if (typeof data.version !== 'undefined') {
			this.version = data.version || '';
		}
		if (typeof data.resetAiCreditsAt !== 'undefined') {
			this.resetAiCreditsAt = data.resetAiCreditsAt;
		}

		if (data.topUps) {
			this.topUps = data.topUps || [];
		}

		if (data.subscriptions) {
			const oldPlan = this.activePlan;

			const currentPlan = data.subscriptions.find((item) => item.type === 'active_plan');

			if (currentPlan) {
				this.subscription = new Subscription(currentPlan);
			} else {
				this.loggerService.logError(
					'No currentPlan subscription',
					{
						subscriptions: JSON.stringify(data.subscriptions),
					},
					Severity.CRITICAL,
				);
			}

			const higherPlanTrial = data.subscriptions.find(
				(item) => item.type === 'higher_plan_trial',
			);
			if (higherPlanTrial) {
				this.higherPlanTrialSubscription = new Subscription(higherPlanTrial);
			} else {
				this.higherPlanTrialSubscription = null;
			}

			if (currentPlan?.schedule) {
				this.scheduledSubscriptionChanges = currentPlan.schedule;
			} else {
				this.scheduledSubscriptionChanges = null;
			}

			const newPlan = this.activePlan;

			if (newPlan && oldPlan?.id !== newPlan.id) {
				this.onPlanChange(oldPlan, newPlan);
			}
		}
	}

	updateFeatureInfo(data: Billing.FeatureInfo): void {
		if (data.boards) {
			this.boardsCurrent = data.boards.current || 0;
		}
		if (data.sources) {
			this.sourcesCurrent = data.sources.current || 0;
		}
		if (data.advanced_data_sources) {
			this.advancedDataSourcesCurrent = data.advanced_data_sources.current || 0;
		}
		if (data.sql_sources) {
			this.sqlSourcesCurrent = data.sql_sources.current || 0;
		}
		if (data[BillingFeature.SHEET_SOURCES]) {
			this.sheetSourcesCurrent = data[BillingFeature.SHEET_SOURCES].current || 0;
		}
		if (data.users) {
			this.usersCurrent = data.users.current || 0;
		}
		if (data.clients) {
			this.clientsCurrent = data.clients.current || 0;
		}
		if (data.custom_metrics) {
			this.customMetricsCurrent = data.custom_metrics.current || 0;
		}
		if (data.storage) {
			this.storageCurrent = data.storage.current || 0;
		}
		if (data[BillingFeature.AI_CREDITS]) {
			this.aiCreditsCurrent = data[BillingFeature.AI_CREDITS].current || 0;
		}

		if (data[BillingFeature.REPORTS_BASIC]) {
			this.reportsCurrent = data.reports_basic.current || 0;
		}

		this.features.updateFeatureData(data);
		this.accountService.updateWhitelabelingEnabledFromBilling(); // updates "account.whiteLabelingEnabled"
	}

	prepareAddonsList(billingCycle?: BillingCycle): Billing.Addon[] {
		const items: Billing.Addon[] = [];

		let addonIds: BillingAddonType[] = [];
		if (this.activePlan?.isFree) {
			addonIds = this.allAddons.filter((id) => id !== BillingAddonType.CLIENT_PACK);
		} else {
			addonIds = this.availableAddonsForActivePlan;
		}

		addonIds.forEach((addonId) => {
			const item = this.getAddonInfo(addonId, billingCycle);
			if (item) {
				items.push(item);
			}
		});

		return items;
	}

	prepareServicesList(): Billing.ServiceInfo[] {
		const serviceIds = Object.keys(BillingService.serviceInfos) as BillingServiceType[],
			items: Billing.ServiceInfo[] = [];

		items.push({
			id: BillingServiceType.RESELLER_PARTNER,
			name: 'Solutions partner program',
			price: null,
			icon: 'Handshake',
			description: 'Learn BI as a service, increase retention, and build a strategic edge.',
			bookCallLink: Defaults.RESELLER_PARTNER_URL,
			learnMoreUrl: 'https://databox.com/reseller',
			isPurchasable: false,
		});

		items.push({
			id: BillingServiceType.CUSTOM_METRIC_DASHBOARD_CREATION,
			name: 'Custom metrics & dashboard creation',
			price: null,
			priceFormatted: 'Custom',
			icon: 'UserCenter',
			description:
				'Get expert help setting up your initial datasets, dashboards, and reports, plus training so you can confidently manage and expand your setup.',
			bookCallLink:
				'https://meetings.hubspot.com/databox-meeting/databox-onboarding?uuid=b6ad2313-9716-496f-95b2-f5f4ccde6ce3',
			learnMoreUrl: 'https://databox.com/services',
			isPurchasable: false,
		});

		serviceIds.forEach((serviceId) => {
			const item = this.getServiceInfo(serviceId);
			if (item) {
				item.isPurchasable = this.hasPayment;
				items.push(item);
			}
		});

		return items;
	}

	prepareAddonsListForPlan(plan: BillingPlan): Billing.Addon[] {
		const items: Billing.Addon[] = [],
			addonIds = plan.availableAddonsAndTopUps.map((addon) => addon.id);

		addonIds.forEach((addonId) => {
			const item = this.getAddonInfo(addonId);
			if (item) {
				items.push(item);
			}
		});

		return items;
	}

	startCancellation(trackingOrigin: TrackingOriginForSubscriptionChangePopup) {
		this.flowCancellationService.trackingOriginForCancellationFlowStarted =
			'cancel_subscription';
		this.flowCancellationService.trackingOriginForBillingPeriodChangePopup = trackingOrigin;
		this.flowCancellationService.startCancellation();

		this.analyticsService.event(TrackingEvent.CANCEL_INITATED, {
			type: this.getTrackingSubscriptionType(),
		});
	}

	openConfirmPopup(props: PopupData.Billing.Confirm) {
		this.popupService
			.createModal(BillingPopupConfirmComponent, {
				popupClass: 'popup-type-billing-confirm',
			})
			.open(props);
	}

	openInvoicesPopup(): void {
		this.popupService
			.createModal(BillingPopupInvoicesComponent, {
				popupClass: 'popup-type-billing-invoices',
			})
			.open();
	}

	openInvoiceDowngradePopup(): void {
		this.popupService
			.createModal(BillingPopupInvoiceDowngradeComponent, {
				popupClass: 'popup-type-billing-invoice-downgrade',
			})
			.open();
	}

	openPaymentErrorPopup(): void {
		schedule('afterRender', this, () => {
			const popup = this.popupService.createModal(BillingPopupPaymentFailedComponent, {
				popupClass: 'popup-type-billing-payment-failed title-bigger',
				closeOnOverlayClick: this.currentUserService.isAdmin,
				showClose: this.currentUserService.isAdmin,
			});
			popup.open();
		});
	}

	openFreeTrialRequestPopup(
		feature: string | null,
		origin?: TrackingOriginTypeForTrialPopupOpened | null,
	): void {
		this.popupService
			.createModal(BillingPopupFreeTrialRequestComponent, {
				popupClass: 'popup-free-trial-request',
			})
			.open({
				feature,
				origin,
			});
	}

	openAddonPurchasePopup(
		model: Billing.Addon,
		origin?: TrackingOriginTypeForTrialPopupOpened | null,
	) {
		if (model.id === BillingAddonType.AI_CREDITS) {
			this.openAiCreditsPurchasePopup(origin);
			return;
		}

		if (model.id === BillingAddonType.CLIENT_PACK) {
			this.openClientPacksPurchasePopup(origin);
			return;
		}

		this.popupService
			.createModal(BillingPopupAddonPurchaseComponent, {
				popupClass: 'popup-type-billing-addon-purchase',
				allowStacked: true, // because of: navigation/popup-fullscreen-loader
			})
			.open({ model });
	}

	openActivityLogAddonPurchasePopup() {
		const addon = this.getAddonInfo(BillingAddonType.ADVANCED_SECURITY);

		if (addon) {
			this.openAddonPurchasePopup(addon);
		}
	}

	openAiCreditsPurchasePopup(origin?: TrackingOriginTypeForTrialPopupOpened | null) {
		this.popupService
			.createModal(BillingPopupAiCreditsPurchaseComponent, {
				popupClass: 'popup-type-billing-ai-credits',
				allowStacked: true, // because of: navigation/popup-fullscreen-loader
			})
			.open({ origin });
	}

	// the endpoint answers only for admins of the agency space
	get canViewAiCreditsUsageByAccount(): boolean {
		return this.accountService.isAgency && this.currentUserService.isAdmin;
	}

	// without a period, the backend uses the current AI credits billing window
	fetchAiCreditsUsage(
		range?: APIData.Billing.AiCreditsUsageRequest,
	): Promise<APIData.Billing.AiCreditsUsageResponse> {
		return this.requestService.api<APIData.Billing.AiCreditsUsageResponse>(
			'GET',
			'/chat/credits/usage',
			range ?? null,
			{ withoutPrefix: true },
		);
	}

	openAiCreditsUsagePopup() {
		this.popupService
			.createModal(BillingPopupAiCreditsUsageComponent, {
				popupClass: 'popup-type-billing-ai-credits-usage',
			})
			.open();
	}

	openClientPacksPurchasePopup(origin?: TrackingOriginTypeForTrialPopupOpened | null) {
		this.popupService
			.createModal(BillingPopupClientPacksPurchaseComponent, {
				popupClass: 'popup-type-billing-client-packs',
				allowStacked: true, // because of: navigation/popup-fullscreen-loader
			})
			.open({ origin });
	}

	openAddonCancelPopup(model: Billing.Addon) {
		this.popupService
			.createModal(BillingPopupAddonCancelComponent, {
				popupClass: 'popup-type-billing-addon-cancel',
			})
			.open({ model });
	}

	openServicesAndAddonsPopup() {
		this.popupService
			.createModal(BillingPopupServicesAndAddonsComponent, {
				popupClass: 'popup-type-billing-services-and-addons',
			})
			.open();
	}

	openServicesConfirmPopup(model: Billing.ServiceInfo) {
		this.popupService
			.createModal(BillingPppupServicesConfirmComponent, {
				popupClass: 'popup-type-billing-services-confirm',
			})
			.open({ model });
	}

	openCardExpiringPopup() {
		this.popupService
			.createModal(BillingPopupCardExpiringComponent, {
				popupClass: 'popup-type-billing-card-expiring title-bigger',
				closeOnOverlayClick: false,
			})
			.open();
	}

	// currently unused
	createHigherPlanTrialExpiredPopup() {
		return this.popupService.createModal(BillingPopupHigherPlanTrialExpiredComponent, {
			popupClass: 'popup-type-billing-higher-plan-trial-expired',
		});
	}

	openTrialActivatedPopup(model: BillingPlan) {
		this.popupService
			.createModal(BillingPopupTrialActivatedComponent, {
				popupClass: 'popup-type-billing-trial-activated',
			})
			.open({ model });
	}

	openSecondTrialPopup(hasSecondTrialStarted: boolean) {
		this.popupService
			.createModal(BillingPopupSecondTrialComponent, {
				popupClass: 'popup-type-billing-second-trial',
			})
			.open({ hasSecondTrialStarted });
	}

	openForbiddenPopup(props: PopupData.Billing.ForbiddenFeature) {
		const popup = this.popupService.createModal(BillingPopupForbiddenFeatureComponent, {
			popupClass: 'popup-type-billing-forbidden-feature',
		});

		popup.open(props);

		return popup;
	}

	get canActOnLimitations(): boolean {
		return (
			!this.navigationService.isDatawallApp &&
			this.currentUserService.isAdmin &&
			!this.accountService.isClient &&
			this.currentUserService.isOnboarded
		);
	}

	openRequestUpgradePopup(feature: BillingFeature) {
		const popup = this.popupService.createModal(BillingPopupRequestUpgradeComponent, {
			popupClass: 'popup-type-request-upgrade',
		});

		const predefinedEmails: string[] = [],
			agencyAdmin = this.accountService.isClient
				? this.mmUsersService.agencyAdminRecordForClientSpace
				: this.accountService.accountOwner;
		if (agencyAdmin) {
			predefinedEmails.push(agencyAdmin.email);
		}

		popup.open({
			recipients: predefinedEmails,
			feature,
		});

		return popup;
	}

	openPlanUpgradeForFeaturePopup(
		feature: BillingFeature,
		trackingOrigin?: TrackingOriginTypeForTrialPopupOpened | null,
		onPlanUpgrade?: (hasUpgraded: boolean) => void,
		useOriginForTrackingFeature?: boolean,
		onNavigateToClick?: () => void,
	) {
		const popup = this.popupService.createModal(BillingPopupPlanUpgradeFeatureComponent, {
			popupClass:
				'popup-type-billing-plan-upgrade-common popup-type-billing-plan-upgrade-feature',
		});

		popup.open({
			feature,
			trackingOrigin,
			useOriginForTrackingFeature,
			onPlanUpgrade,
			onNavigateToClick,
		});

		return popup;
	}

	checkToOpenGrowthPaidTrialPopup() {
		if (
			this.userSettingsService.hasSeenGrowthTrialOfferPopup ||
			!this.isPayingCustomer ||
			!this.flowPromotionsService.promotions.getHigherPlanAutomatedTrialOfferOptionPromotionForPlanType(
				PlanType.GROWTH,
			)
		) {
			return;
		}

		this.openGrowthPaidTrialPopup();
	}

	openGrowthPaidTrialPopup() {
		const popup = this.popupService.createModal(
			BillingPromotionsGrowth2024PopupPlanUpgradeOfferComponent,
			{
				popupClass:
					'popup-type-billing-plan-upgrade-common popup-type-billing-plan-upgrade-offer',
			},
		);

		return popup.open();
	}

	requestAutomatedPayingTrial(planId: string, promotionId: PromotionIdentifier) {
		const data: ChangePlanData = {
			planId,
			trialing: true,
			promotionId,
		};

		return this.doPlanSwitchEndpoint(data);
	}

	handleLimitations(
		feature: BillingFeature,
		origin?: TrackingOriginTypeForTrialPopupOpened | null,
		sourceType?: SourceType,
		useOriginForTrackingFeature?: boolean,
		onPlanUpgrade?: (hasUpgraded: boolean) => void,
	): boolean {
		const isClient = this.accountService.isClient,
			isAdmin = this.currentUserService.isAdmin,
			hasNonAdminPermissionToBuy =
				feature === BillingFeature.AI_CREDITS &&
				!this.accountService.onlyAdminsCanBuyCredits;

		const canBuy = !isClient && (isAdmin || hasNonAdminPermissionToBuy);

		if (!this.hadTrial && !!this.activePlan?.isFree && !this.accountService.isClient) {
			this.openFreeTrialRequestPopup(feature, origin); // starting first trial
		} else if (canBuy) {
			const targetPlanWithFeature = this.getNextPlanForFeatureWithSameBillingCycle(
					feature,
					true,
				),
				payingTrialPromotions = targetPlanWithFeature
					? this.flowPromotionsService.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(
							targetPlanWithFeature.planType,
						)
					: [];
			if (
				targetPlanWithFeature &&
				payingTrialPromotions.length &&
				feature !== BillingFeature.AI_CREDITS
			) {
				this.flowPromotionsService.openStartHigherPlanAutomatedTrialPopup(
					targetPlanWithFeature,
					feature,
					origin,
					useOriginForTrackingFeature,
				);
			} else if (feature === BillingFeature.ACTIVITY_LOG) {
				this.openActivityLogAddonPurchasePopup();
			} else {
				// first check if it's and addon availalbe on the current plan
				// if not, find the first plan upgrade that has the feature
				// if none found, it's possibly an addon that's not available on current plan

				const addonId = this.getAddonIdForFeature(feature);
				const addonAvailableOnCurrentPlan =
					addonId && this.activePlan?.isAddonAvailable(addonId);

				if (addonAvailableOnCurrentPlan) {
					// offer addon first
					const addonInfo = this.getAddonInfo(addonId)!;
					this.openAddonPurchasePopup(addonInfo, origin);
				} else if (isAdmin && targetPlanWithFeature) {
					// offer plan upgrade
					this.openPlanUpgradeForFeaturePopup(
						feature,
						origin,
						onPlanUpgrade,
						useOriginForTrackingFeature,
					);
					return true;
				} else if (addonId) {
					// no plan, addon exists, but not for current plan (e.g. Free)
					this.infolayerService.error('This addon is not available on current plan!');
				} else {
					this.infolayerService.error('This feature is unavailable');
					this.loggerService.logError(
						'[BILLING] No plan or addon for feature',
						{
							feature,
						},
						Severity.CRITICAL,
					);
				}
			}
		} else {
			this.openForbiddenPopup({
				feature,
				sourceType,
				trackingOrigin: origin,
				useOriginForTrackingFeature,
			});
		}

		return false;
	}

	handleLimitationsForPlanUpgrade(
		feature: BillingFeature,
		origin?: TrackingOriginTypeForTrialPopupOpened | null,
	): Promise<boolean> {
		if (this.checkIfCan(feature)) {
			return Promise.resolve(true);
		}

		return new Promise((resolve) => {
			const awaitsUpgrade = this.handleLimitations(
				feature,
				origin,
				undefined,
				undefined,
				resolve,
			);

			if (!awaitsUpgrade) {
				resolve(false);
			}
		});
	}

	handlePlanUpgradeForFeature(featureName: BillingFeature): Promise<boolean> {
		const currentPlan = this.activePlan;
		const targetPlan = this.getNextPlanForFeatureWithSameBillingCycle(featureName) || null;

		return new Promise((resolve) => {
			if (currentPlan?.id === targetPlan?.id) {
				this.navigationService.transitionToRoute('settings.billing');
				resolve(false);
			} else if (targetPlan) {
				this.switchPlan(targetPlan);
				resolve(true);
			}
			resolve(false);
		});
	}

	private createExpiredPopup() {
		return this.popupService.createModal(BillingPopupExpiredTrialComponent, {
			popupClass: 'popup-type-billing-expired',
		});
	}

	// Annual commitment
	get hasActiveCommitment() {
		const until = this.commitment?.until;
		return !!until && until > this.timerService.currentTs;
	}

	get commitmentUntilFormatted() {
		const until = this.commitment?.until;
		return until ? getFormattedTime(until, this.currentUserService.model?.timezone) : '';
	}

	get commitmentActionDateOffsetDays() {
		const actionDate = this.commitment?.actionDate;
		const until = this.commitment?.until;
		if (!actionDate || !until) {
			return 0;
		}
		return moment.unix(until).diff(moment.unix(actionDate), 'days');
	}

	get blockDowngradeOrCancelDueCommitment() {
		const actionDate = this.commitment?.actionDate;
		return this.hasActiveCommitment && !!actionDate && actionDate > this.timerService.currentTs;
	}

	handleCommitmentRestriction() {
		if (this.blockDowngradeOrCancelDueCommitment) {
			const popup = this.popupService.createModal(BillingPopupAnnualCommitmentInfoComponent, {
				popupClass: 'popup-type-billing-annual-commitment-info',
			});
			popup.open();

			return true;
		}
		return false;
	}

	// Tracking
	private triggerChangeBillingPeriodEvent(
		plan: BillingPlan,
		trackingOrigin?:
			TrackingOriginForSubscriptionChangePopup | TrackingOriginTypeForTrialPopupOpened,
		isFromPopup = false,
	): void {
		if (this.activePlan?.billingCycle !== plan.billingCycle) {
			this.analyticsService.event(TrackingEvent.CHANGE_BILLING_PERIOD, {
				origin: trackingOrigin,
				old_period: this.activePlan?.billingCycle || 'free',
				new_period: plan.billingCycle,
				type: this.getTrackingSubscriptionType(),
				from_popup: isFromPopup,
			});
		}
	}

	private triggerPlanChangeEvent(
		plan: BillingPlan,
		feature: BillingFeature | null = null,
		trackingOrigin?:
			TrackingOriginTypeForTrialPopupOpened | TrackingOriginForSubscriptionChangePopup,
		isFromPopup = false,
	): void {
		if (this.activePlan?.planType !== plan.planType) {
			const origin = trackingOrigin || feature;
			this.analyticsService.event(TrackingEvent.PLAN_CHANGE, {
				origin,
				plan: plan.id,
				old_plan: this.activePlan?.id,
				old_period: this.activePlan?.billingCycle || 'free',
				new_period: plan.billingCycle,
				type: this.getTrackingSubscriptionType(),
				from_popup: isFromPopup,
			});
		}
	}

	getOriginByFeatureForTrialPopupOpened(
		featureName: string | null,
	): TrackingOriginTypeForTrialPopupOpened | null {
		switch (featureName) {
			case BillingFeature.BOARDS:
			case BillingFeature.SOURCES:
			case BillingFeature.USERS:
			case BillingFeature.CUSTOM_METRICS:
			case BillingFeature.STORAGE:
				return 'count_limitation';
			case BillingFeature.QB:
			case BillingFeature.SCRIPTS:
			case BillingFeature.SSO:
			case BillingFeature.HISTORICAL_DATA:
			case BillingFeature.DATA_REFRESH_INTERVAL:
			case BillingFeature.CUSTOM_RANGE:
			case BillingFeature.API_METRIC_BUILDER:
			case BillingFeature.ALERTS:
			case BillingFeature.GOALS:
			case BillingFeature.SHARING:
				return 'feature_limitation';
			case BillingFeature.ADVANCED_REPORTS:
				return 'feature_limitation_reports';
			case BillingFeature.PREMIUM_SOURCES:
				return 'datasource_limitation';
			default:
				return null;
		}
	}

	getTrackingSubscriptionType(): TrackingOriginSubscriptionType {
		let type: TrackingOriginSubscriptionType = 'free';

		if (
			this.isPayingCustomer ||
			this.isCanceled /* Use case: https://app.asana.com/0/0/1203586736005185/f */
		) {
			type = 'customer';
		} else if (this.isTrial) {
			type = 'trial';
		}

		return type;
	}

	getOriginForCallInitiated(origin: 'help' | 'feature_page'): string {
		let formattedOrigin = `${origin}_paid`;

		if (this.activePlan?.isFree || this.isTrial) {
			formattedOrigin = `${origin}_free`;
		}

		return formattedOrigin;
	}

	trackLimitationPopupEvent({
		feature,
		trackingOrigin,
	}: {
		feature: BillingFeature;
		trackingOrigin?: string;
	}) {
		const type = this.getTrackingSubscriptionType();
		this.analyticsService.event(TrackingEvent.LIMITATION_POPUP, {
			feature: trackingOrigin || feature,
			type,
		});
	}

	// SUBSCRIPTION PREVIEW

	requestSubscriptionPreview(
		planProductId: string,
		selectedAddons: { id: BillingAddonType; quantity: number }[] = [],
		skipCache = false,
	) {
		// Create a unique cache key
		const cacheKey = this.generateCacheKey(planProductId, selectedAddons);

		// Check the cache first
		if (!skipCache && this.subscriptionPreviewCache.has(cacheKey)) {
			return Promise.resolve(this.subscriptionPreviewCache.get(cacheKey));
		}

		const extras = selectedAddons.map((addonData) => {
			return {
				productId: addonData.id,
				quantity: addonData.quantity,
			};
		});

		const data: APIData.Billing.SubscriptionPreviewRequest = {
			productId: planProductId,
		};

		if (extras.length) {
			data.extras = extras;
		}

		return this.requestService
			.api<APIData.Billing.SubscriptionPreview>(
				'POST',
				'/billing/preview-subscription',
				data,
				{
					skipShowingErrorInfolayer: true,
				},
			)
			.then((reply) => {
				// Store the response in the cache
				this.subscriptionPreviewCache.set(cacheKey, reply);
				return reply;
			});
	}

	clearSubscriptionPreviewCache() {
		this.subscriptionPreviewCache.clear();
	}

	checkToAddBillingIssuePopup() {
		const routeBlacklist = ['settings.billing.*'];

		const expiredTrialPopup = this.createExpiredPopup();
		this.automatedPopupQueueService.queuePopup({
			popup: expiredTrialPopup,
			routeBlacklist,
			checkAddConditions: () => {
				return this.isExpiredTrial;
			},
			checkStartConditions: () => {
				return this.isExpiredTrial && !this.isRequestSecondTrialInProgress;
			},
			skipCooldown: true,
		});

		const reactivatePopup = this.flowCancellationService.createReactivatePopup();
		this.automatedPopupQueueService.queuePopup({
			popup: reactivatePopup,
			routeBlacklist,
			checkAddConditions: () => {
				return this.isPaused;
			},
			checkStartConditions: () => {
				return this.isPaused;
			},
			skipCooldown: true,
		});
	}

	private subscriptionPreviewCache = new Map<string, APIData.Billing.SubscriptionPreview>();

	private generateCacheKey(planProductId: string, selectedAddonIds?: unknown[]): string {
		const sortedAddonIds = selectedAddonIds?.length
			? `|${JSON.stringify(selectedAddonIds)}`
			: '';
		return `${planProductId}${sortedAddonIds}`;
	}

	// FEATURES

	getFeatureName(feature: BillingFeature): string {
		return ActiveFeatures.featureInfos[feature]?.name || '';
	}

	getAddonIdForFeature(feature: BillingFeature): BillingAddonType | null {
		const entry = (
			Object.entries(this.addonsToFeatures) as [BillingAddonType, BillingFeature[]][]
		).find(([, features]) => features.includes(feature));
		return entry?.[0] ?? null;
	}

	getAvailableAddonForFeatureOnPlan(
		plan: BillingPlan,
		feature: BillingFeature,
	): Billing.Addon | null {
		const addonId = this.getAddonIdForFeature(feature);
		if (addonId && plan.isAddonAvailable(addonId)) {
			const addonInfo = this.getAddonInfo(addonId);
			if (addonInfo) {
				return addonInfo;
			}
		}
		return null;
	}

	// https://github.com/databox/billing-service/blob/master/src/main/resources/config/billing.feature.set.json
	private addonsToFeatures = {
		[BillingAddonType.WHITE_LABELING]: [BillingFeature.WHITE_LABELING],
		[BillingAddonType.PRIORITY_SUPPORT]: [BillingFeature.PRIORITY_SUPPORT],
		[BillingAddonType.PRIORITY_SYNC]: [BillingFeature.PRIORITY_SYNC],
		[BillingAddonType.FISCAL_CALENDARS]: [BillingFeature.FISCAL_CALENDARS],
		[BillingAddonType.ADVANCED_SECURITY]: [
			BillingFeature.ADVANCED_SECURITY,
			BillingFeature.ACTIVITY_LOG,
			BillingFeature.SSO,
			BillingFeature.SSO_FORCED,
			BillingFeature.MFA_FORCED,
			BillingFeature.LOGIN_RECORDS,
		],
		[BillingAddonType.OKR]: [BillingFeature.OKR],
		[BillingAddonType.AI_CREDITS]: [BillingFeature.AI_CREDITS],
		[BillingAddonType.CLIENT_PACK]: [BillingFeature.CLIENTS, BillingFeature.SOURCES],
	};

	getPlanFeatureItemInfo(item: PlanFeatureItem): PlanFeatureItemInfo {
		return BillingService.planFeatureItemInfos[item];
	}

	// Order in which enabled feature items are surfaced in the plan card's
	// "Everything in X, plus:" list.
	static planFeatureItemsOrder: PlanFeatureItem[] = [
		PlanFeatureItem.CLOUD_INTEGRATIONS,
		PlanFeatureItem.AI_ANALYST,
		PlanFeatureItem.PERFORMANCE_SUMMARIES,
		PlanFeatureItem.DASHBOARDS_REPORTS,
		PlanFeatureItem.GOVERNED_SEMANTIC_LAYER,
		PlanFeatureItem.CUSTOM_INTEGRATIONS,
		PlanFeatureItem.ARTIFACTS,
		PlanFeatureItem.PREBUILT_SKILLS,
		PlanFeatureItem.MCP_SERVER,
		PlanFeatureItem.DATABOX_API,
		PlanFeatureItem.ROUTINES,
		PlanFeatureItem.CUSTOM_SKILLS,
		PlanFeatureItem.SHARING,
		PlanFeatureItem.AGENTS,
		PlanFeatureItem.MCP_CONNECTORS,
	];

	getEnabledPlanFeatureItems(
		plan: BillingPlan,
		previousPlan?: BillingPlan,
	): EnabledPlanFeatureItems {
		const title = previousPlan ? `Everything in ${previousPlan.name}, plus:` : 'Includes:';

		const isEnabled = (p: BillingPlan, item: PlanFeatureItem) => {
			const value = p.getPlanFeatureItemValue(item);
			return value.flag === true || (!!value.text && value.text !== '0');
		};

		const items = BillingService.planFeatureItemsOrder
			.filter(
				(item) => isEnabled(plan, item) && !(previousPlan && isEnabled(previousPlan, item)),
			)
			.map((item) => this.getPlanFeatureItemInfo(item));

		return { title, items };
	}

	customPlanFeatureItems = [
		'We audit your setup',
		'We design your AI stack',
		'We build and integrate it',
		'We optimize it as your needs grow',
		"You're live, with an embedded engineer",
	];

	// getAddonInfo adds prices and state
	static addonInfos: Partial<Record<BillingAddonType, AddonBaseInfo>> = {
		[BillingAddonType.WHITE_LABELING]: {
			name: ActiveFeatures.featureInfos[BillingFeature.WHITE_LABELING].name,
			description: ActiveFeatures.featureInfos[BillingFeature.WHITE_LABELING].description,
			learnMoreUrl: 'https://help.databox.com/white-label-your-account',
			icon: 'Paint',
		},
		[BillingAddonType.PRIORITY_SUPPORT]: {
			name: ActiveFeatures.featureInfos[BillingFeature.PRIORITY_SUPPORT].name,
			description: ActiveFeatures.featureInfos[BillingFeature.PRIORITY_SUPPORT].description,
			learnMoreUrl: 'https://help.databox.com/getting-support-from-databox',
			icon: 'Phone',
		},
		[BillingAddonType.PRIORITY_SYNC]: {
			name: ActiveFeatures.featureInfos[BillingFeature.PRIORITY_SYNC].name,
			description: ActiveFeatures.featureInfos[BillingFeature.PRIORITY_SYNC].description,
			learnMoreUrl: 'https://help.databox.com/change-the-data-sync-frequency',
			icon: 'Refresh',
		},
		[BillingAddonType.FISCAL_CALENDARS]: {
			name: ActiveFeatures.featureInfos[BillingFeature.FISCAL_CALENDARS].name,
			description: ActiveFeatures.featureInfos[BillingFeature.FISCAL_CALENDARS].description,
			learnMoreUrl: 'https://help.databox.com/switch-to-a-fiscal-calendar',
			icon: 'Calendar',
		},
		[BillingAddonType.ACTIVITY_LOG]: {
			name: ActiveFeatures.featureInfos[BillingFeature.ACTIVITY_LOG].name,
			description: ActiveFeatures.featureInfos[BillingFeature.ACTIVITY_LOG].description,
			learnMoreUrl: 'https://help.databox.com/review-the-actions-taken-on-your-account',
			icon: 'ActivityLog',
		},
		[BillingAddonType.ADVANCED_SECURITY]: {
			name: ActiveFeatures.featureInfos[BillingFeature.ADVANCED_SECURITY].name,
			description: ActiveFeatures.featureInfos[BillingFeature.ADVANCED_SECURITY].description,
			learnMoreUrl: 'https://help.databox.com/review-the-actions-taken-on-your-account',
			icon: 'Shield',
		},
		[BillingAddonType.OKR]: {
			name: ActiveFeatures.featureInfos[BillingFeature.OKR].name,
			description: ActiveFeatures.featureInfos[BillingFeature.OKR].description,
			learnMoreUrl: 'https://databox.com/okr-software',
			icon: 'Target',
		},
		[BillingAddonType.AI_CREDITS]: {
			name: 'AI credit top-up',
			description:
				"AI credits fuel Databox's AI features, including Genie and the MCP server. Actions like generating insights, building dashboards, and asking questions about your data all consume credits from a shared monthly pool.",
			learnMoreUrl: 'https://help.databox.com/understanding-ai-credits',
			icon: 'Ai',
			alwaysMonthly: true,
		},
		[BillingAddonType.CLIENT_PACK]: {
			name: 'Account pack',
			description:
				'Expand your organization with 1 additional account, 5 data sources, and 50 AI credits.',
			learnMoreUrl: 'https://help.databox.com/scale-your-agency-plan-with-client-packs',
			icon: 'Clients',
		},
	};

	static serviceInfos: Partial<Record<BillingServiceType, ServiceBaseInfo>> = {
		// ADD INFO FOR SERVICES THAT ARE ALSO RETURNED ON PLANS HERE (getServiceInfo will add that info)
		// EXAMPLE BELOW
		// [BillingServiceType.GUIDED_ONBOARDING]: {
		// 	name: 'Guided Onboarding',
		// 	description:
		// 		'Training and consulting on setting up and rolling out Databox to your company or clients. Includes 6 training calls over Zoom and Priority Support for 3 months.',
		// 	bookCallLink: 'https://meetings.hubspot.com/databox-meeting/databox-onboarding',
		// 	learnMoreUrl: 'https://databox.com/services#onboarding',
		// 	icon: 'UserCenter',
		// },
	};

	// Name + description (tooltip) for each feature-comparison item shown in the plan
	// comparison table. Kept here (like addonInfos) so the copy lives in one place
	// and the components just declare which items to show and in what order.
	static planFeatureItemInfos: Record<PlanFeatureItem, PlanFeatureItemInfo> = {
		[PlanFeatureItem.DATA_SOURCES]: {
			name: 'Data sources included',
			description:
				'The total number of data sources on your plan. Each account or property counts as one, so 3 GA4 properties equal 3 data sources.',
		},
		[PlanFeatureItem.CLOUD_INTEGRATIONS]: {
			name: 'Cloud integrations',
			description:
				'Sync data from popular tools like HubSpot, Google Analytics 4, Facebook Ads, and more.',
		},
		[PlanFeatureItem.CUSTOM_INTEGRATIONS]: {
			name: 'Custom data integrations',
			description:
				'Pull data from Google Sheets, Excel, SQL and NoSQL databases, and supported APIs through custom integrations.',
		},
		[PlanFeatureItem.DATASETS]: {
			name: 'Datasets',
			description:
				'Create custom tables from one or more data sources to organize the data you need for analysis and reporting.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.DATABOX_API]: {
			name: 'API access',
			description:
				'Use the Databox API to send and retrieve data, automate workflows, and manage your Databox account.',
		},
		[PlanFeatureItem.HISTORICAL_DATA]: {
			name: 'Historical data',
			description: 'The maximum amount of historical data available in your visualizations.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.MAX_SYNC_FREQUENCY]: {
			name: 'Max sync frequency',
			description:
				'The fastest available sync frequency for your data sources. Availability may vary by provider.',
		},
		[PlanFeatureItem.AI_ANALYST]: {
			name: 'AI analyst (Genie)',
			description:
				'Ask questions about your performance in plain language and get instant answers.',
		},
		[PlanFeatureItem.AI_CREDITS]: {
			name: 'AI credits included / mo',
			description:
				'The number of AI credits available each month to power Genie and the MCP server. Credits are shared across the account and reset monthly.',
		},
		[PlanFeatureItem.PERFORMANCE_SUMMARIES]: {
			name: 'Performance summaries',
			description:
				'Get an AI-generated text summary of any metric or group of metrics, turning your data into readable insights at a glance.',
		},
		[PlanFeatureItem.MCP_SERVER]: {
			name: 'MCP server',
			description:
				'Connect Databox to AI tools like ChatGPT or Claude to access trusted metrics and trigger actions, without leaving your workflow.',
		},
		[PlanFeatureItem.PREBUILT_METRICS_TEMPLATES]: {
			name: 'Prebuilt metrics and templates',
			description:
				'Get started instantly with hundreds of ready-made metrics, dashboard templates, and report templates, customizable to fit any workflow.',
		},
		[PlanFeatureItem.ANOMALY_DETECTION]: {
			name: 'Anomaly detection',
			description: 'Catch spikes, drops, and anomalies in your most important metrics.',
		},
		[PlanFeatureItem.DASHBOARDS_REPORTS]: {
			name: 'Dashboards & reports',
			description:
				'Build fully customizable dashboards and reports using a drag-and-drop interface, multiple visualization types, and flexible presentation or pageless formats.',
		},
		[PlanFeatureItem.CUSTOM_METRICS]: {
			name: 'Custom metrics',
			description:
				'Create custom metrics by adding calculations, filters, segments, and dimensions to your data.',
		},
		[PlanFeatureItem.GOALS]: {
			name: 'Goals',
			description:
				'Set targets for your team or company, break them down into bite-sized steps, and monitor your progress.',
		},
		[PlanFeatureItem.ACCOUNT_TEMPLATES]: {
			name: 'Account templates',
			description:
				'Turn dashboards and reports into reusable templates for faster setup across accounts.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.LOOPED_DATABOARDS]: {
			name: 'Looped dashboards',
			description:
				'Combine multiple dashboards into a rotating loop for continuous display on TVs and shared screens.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.REPORT_SLIDE_LIBRARY]: {
			name: 'Report slide library',
			description:
				'Choose from all available slide types when building your presentation reports.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.SSO]: {
			name: 'Single sign-on (SSO)',
			description:
				'Sign in to Databox using your organization’s existing identity provider for centralized access management.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.SHARING]: {
			name: 'Sharing & notifications',
			description:
				'Share dashboards and reports via links, TV, or embeds, and deliver ongoing performance updates through scorecards, alerts, scheduled snapshots, and weekly updates.',
		},
		[PlanFeatureItem.CSV_EXPORT]: {
			name: 'CSV export (datasets)',
			description: 'Prepare data in Databox, then export it for further analysis.',
		},
		[PlanFeatureItem.USERS]: {
			name: 'Users',
			description:
				'Invite team members and control access by assigning roles that define what each person can see and do in Databox.',
		},
		[PlanFeatureItem.WHITE_LABELING]: {
			name: 'White-labeling',
			description:
				'Fully brand the Databox experience with your own domain, email domain, logos, images, and other brand elements.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.SUB_ACCOUNTS]: {
			name: 'Accounts',
			description:
				'Set up dedicated accounts under your organization to streamline and organize your reporting process.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.CHAT_EMAIL_SUPPORT]: {
			name: 'Chat and email support',
			description: 'Get help via chat or email whenever questions come up.',
		},
		[PlanFeatureItem.ACCOUNT_SETUP]: {
			name: 'Solution development & deployment',
			description:
				'Work with Databox experts to build and deploy dashboards, reports, integrations, and other solutions tailored to your needs.',
		},
		[PlanFeatureItem.SECURITY_COMPLIANCE_REVIEW]: {
			name: 'Security & compliance review',
			description:
				'Complete security questionnaires and access compliance documentation to satisfy internal or regulatory requirements.',
		},
		[PlanFeatureItem.GOVERNED_SEMANTIC_LAYER]: {
			name: 'Governed semantic layer',
			description:
				'Define consistent meaning and context for your data with dataset and column descriptions, data types, and synonyms so people and AI can interpret it reliably.',
		},
		[PlanFeatureItem.DATA_LINEAGE]: {
			name: 'Data lineage / audit trail',
			description:
				'Trace where your data comes from and how it changes as it moves through Databox.',
		},
		[PlanFeatureItem.DATASET_TRANSFORMATIONS]: {
			name: 'Dataset transformations',
			description:
				'Modify datasets to fit your reporting needs. Merge and filter data, add calculated columns, and reorder or hide columns.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.VERIFY_BADGE_ASSETS]: {
			name: 'Verify & badge assets',
			description:
				'Mark trusted metrics, datasets, and dashboards as verified so teams can easily identify approved, reliable definitions across Databox.',
		},
		[PlanFeatureItem.ARTIFACTS]: {
			name: 'Artifacts',
			description:
				'Create reusable outputs from Genie, such as analyses, plans, reports, and other structured deliverables based on your data.',
		},
		[PlanFeatureItem.PREBUILT_SKILLS]: {
			name: 'Prebuilt skills',
			description:
				'Browse and install skills from the Databox Skills marketplace for reporting, analysis, and other common workflows.',
		},
		[PlanFeatureItem.CUSTOM_SKILLS]: {
			name: 'Custom skills',
			description:
				'Create custom skills with your own instructions and workflows to tailor how Genie works for your business.',
		},
		[PlanFeatureItem.GENIE_IN_SLACK]: {
			name: 'Genie in Slack',
			description:
				'Ask Genie questions and get answers about your performance directly in Slack.',
			isSoon: true,
		},
		[PlanFeatureItem.ROUTINES]: {
			name: 'Routines',
			description:
				'Automate recurring workflows by having Genie run predefined tasks on a schedule or trigger.',
		},
		[PlanFeatureItem.AGENTS]: {
			name: 'Agents',
			description:
				'Create autonomous agents that can analyze data and take actions using defined tools, permissions, and instructions.',
			isSoon: true,
		},
		[PlanFeatureItem.MCP_CONNECTORS]: {
			name: 'MCP connectors',
			description:
				'Connect Genie to external tools through MCP so it can access information and take actions across your workflows.',
		},
		[PlanFeatureItem.CUSTOM_DATE_RANGES]: {
			name: 'Custom date ranges',
			description:
				'Analyze your data over any custom time period instead of being limited to predefined date ranges.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.MEDIA_CREATIVES]: {
			name: 'Media creatives',
			description:
				'View ad creatives, such as images, alongside performance data in table visualizations for supported integrations.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.DATA_STORIES]: {
			name: 'Data stories',
			description:
				'Turn performance data into a visual story that combines metrics and narrative for easier reporting and presentation.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.UNBRANDED_DASHBOARDS]: {
			name: 'Unbranded dashboards',
			description:
				'Remove Databox branding from dashboards and customize their appearance to match your brand.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.SMART_ALERTS]: {
			name: 'Smart alerts',
			description:
				'Get notified when Databox detects meaningful anomalies in your metrics, such as unexpected spikes or drops.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.DATA_SEMANTIC_LAYER_SETUP]: {
			name: 'Data & semantic layer setup',
			description:
				'Get help connecting, structuring, and preparing your data, including datasets, metrics, and semantic definitions, so it is ready for reporting and AI.',
		},
		[PlanFeatureItem.AI_IMPLEMENTATION]: {
			name: 'AI implementation',
			description:
				'Get expert help configuring Genie, skills, routines, agents, and MCP connections for your workflows.',
		},
		[PlanFeatureItem.ENABLEMENT]: {
			name: 'Enablement',
			description:
				'Train your team to confidently use Databox, with expert guidance, hands-on sessions, and supporting resources.',
		},
		[PlanFeatureItem.FISCAL_CALENDAR]: {
			name: 'Fiscal calendar',
			description:
				'Align date ranges to your financial year instead of the standard calendar, so performance data reflects your actual business cycles.',
			hideOn2026SeptView: true,
		},
		[PlanFeatureItem.OKRS]: {
			name: 'Objectives and Key Results (OKRs)',
			description:
				"Track company-wide and nested goals and align every team's efforts toward measurable outcomes, all in one place.",
			hideOn2026SeptView: true,
		},
	};
}

declare module '@ember/service' {
	interface Registry {
		billing: BillingService;
	}
}
